import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { participantClubLabel } from "./AdminTournamentRankingPage";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type EliminationFormatConfig,
  type GroupArrangementCategory,
  type Tournament,
  type TournamentDivision,
  type TournamentFormatConfig,
  type TournamentGroup,
  type TournamentGroupMovementEntry,
  type TournamentParticipant,
  type DrawEligibility,
  type MembershipChangePreview,
  type MembershipChangePreviewParams,
  type RrRebuildSummary,
  participantDisplayName,
} from "../lib/api";
import FieldHint from "../components/FieldHint";
import RankMovementBadge from "../components/RankMovementBadge";
import TournamentBottomNav from "../components/TournamentBottomNav";
import { formatDateTime } from "../lib/dateFormat";
import { formatGroupCount, isSingleGroupScope } from "../lib/singleGroup";
import DivisionFormatGuardCard, { divisionNeedsFormat } from "../components/DivisionFormatGuardCard";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import GuideHelpButton from "../components/GuideHelpButton";
import GroupDrawCard from "../components/GroupDrawCard";
import RoundRobinChangeGroupDialog, { type RoundRobinChangeGroupTarget } from "../components/RoundRobinChangeGroupDialog";
import ReplacePlayerDialog from "../components/ReplacePlayerDialog";
import GroupStrayMatchesNotice from "../components/GroupStrayMatchesNotice";
import GroupEditForm, { groupOverCapacity } from "../components/GroupEditForm";
import {
  membershipConfirmText,
  membershipLosesSchedule,
  mergedRrRebuildNotice,
  previewWarnings,
  rrRebuildNotice,
  settleWithin,
  withRrRebuildNotice,
} from "../lib/rrRebuild";
import { initialArrangeDraft } from "../lib/rrSheetOrder";
import { planGroupSlots, POST_DRAW_CHANGE_NOTICE, shuffled } from "../lib/groupDraw";
import { repeatedTournamentGroupNames, tournamentGroupLabel } from "../lib/groupLabels";
import { summarizeGroupAssignmentFailures, type GroupAssignmentFailure } from "../lib/groupAssignmentFailures";
import { firstActiveDivisionByName } from "../lib/tournamentDivisions";
import { participantStatusLabel } from "../lib/playerLabels";

const ACTION_LABELS: Record<string, string> = {
  assigned: "Asignado",
  moved: "Movido",
  removed: "Removido",
};

function formatAction(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function participantLabel(participant: TournamentParticipant | null): string {
  if (!participant) return "Participante";
  // Nombre corto primero (dobles: "Alonso / Bruno") — el label completo de
  // una pareja desbordaba la columna Jugador (pedido Eduardo 2026-08-27).
  return participant.participant_short_label?.trim() || participantDisplayName(participant);
}

function participantFullLabel(participant: TournamentParticipant | null): string {
  if (!participant) return "Participante";
  return participantDisplayName(participant);
}

/** Respaldo del candado «la categoría ya empezó» si el backend no trae texto. */
const ARRANGE_LOCK_FALLBACK = "La categoría ya empezó: ya no se pueden mover jugadores ni cambiar el orden.";

/** Llave del estado de «acomodar» por categoría (null = sin categoría). */
/** Motivos de baja del grupo (maqueta Grupos-Acciones). */
const REMOVE_REASONS = ["Lesión", "Se retiró del torneo", "No se presentó", "Otro"] as const;

function arrangementKey(divisionId: number | null | undefined): string {
  return divisionId == null ? "none" : String(divisionId);
}

/** Orden de los jugadores en la tarjeta del grupo. Grupo acomodado (lugar en
 * la hoja del Round Robin, Eduardo 2026-10-01): el del backend, que ya viene
 * por lugar. Sin acomodar: por rank actual, rank inicial y nombre, como
 * siempre. */
/** Renglón chico de la tarjeta: rank, puntos y categoría; el estado solo si
 * no es «activo» y siempre en español (Eduardo 2026-10-14: salía «active»). */
function chipMeta(participant: TournamentParticipant): string {
  const parts: string[] = [];
  if (participant.current_rank != null) parts.push(`#${participant.current_rank}`);
  if (participant.ranking_points != null) parts.push(`${participant.ranking_points} pts`);
  if (participant.current_category?.name) parts.push(participant.current_category.name);
  const status = (participant.current_status ?? "").toLowerCase();
  if (status && status !== "active" && status !== "confirmed") parts.push(participantStatusLabel(status));
  return parts.join(" · ");
}

function sortGroupAssignments(group: TournamentGroup): NonNullable<TournamentGroup["participants"]> {
  const assignments = [...(group.participants ?? [])];
  if (group.rr_has_positions) return assignments;
  return assignments.sort((a, b) => {
    const currentA =
      a.participant?.current_rank ?? Number.MAX_SAFE_INTEGER;
    const currentB =
      b.participant?.current_rank ?? Number.MAX_SAFE_INTEGER;
    if (currentA !== currentB) return currentA - currentB;
    const initialA =
      a.participant?.initial_rank ?? Number.MAX_SAFE_INTEGER;
    const initialB =
      b.participant?.initial_rank ?? Number.MAX_SAFE_INTEGER;
    if (initialA !== initialB) return initialA - initialB;
    return participantLabel(a.participant).localeCompare(
      participantLabel(b.participant),
      "es-MX",
    );
  });
}

/** Cupo 0 o vacío = sin límite (igual que el servidor). */
export function groupFreeSpots(capacity: number | null | undefined, count: number): number | null {
  if (capacity == null || capacity <= 0) return null;
  return Math.max(capacity - count, 0);
}

/** Siguiente nombre libre de la categoría: Grupo A, B, C… (después, Grupo 27…). */
export function nextGroupNames(existing: readonly string[], count: number): string[] {
  const taken = new Set(existing.map((name) => name.trim().toLowerCase()));
  const out: string[] = [];
  for (let index = 0; out.length < count && index < 500; index += 1) {
    const name = index < 26 ? `Grupo ${String.fromCharCode(65 + index)}` : `Grupo ${index + 1}`;
    if (!taken.has(name.toLowerCase())) {
      taken.add(name.toLowerCase());
      out.push(name);
    }
  }
  return out;
}

/** Búsqueda sin acentos ni mayúsculas. */
function foldText(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function isEliminationFormatConfig(
  formatConfig: TournamentFormatConfig | null,
): formatConfig is TournamentFormatConfig & { config_json: EliminationFormatConfig } {
  return (
    formatConfig?.format_type === "elimination" &&
    typeof (formatConfig.config_json as EliminationFormatConfig).bracket_size === "number"
  );
}

function eliminationPlan(playerCount: number, bracketSize: number | null): {
  drawSize: number;
  preliminaryMatches: number;
  directAdvancers: number;
  nextRoundSize: number;
  overCapacity: boolean;
} {
  const drawSize = bracketSize ?? 0;
  if (drawSize <= 0 || playerCount <= 0) {
    return { drawSize: 0, preliminaryMatches: 0, directAdvancers: 0, nextRoundSize: 0, overCapacity: false };
  }
  const halfRoundSize = drawSize > 2 ? drawSize / 2 : 1;
  if (playerCount > drawSize) {
    const preliminaryMatches = Math.max(playerCount - drawSize, 0);
    return {
      drawSize,
      preliminaryMatches,
      directAdvancers: Math.max(drawSize - preliminaryMatches, 0),
      nextRoundSize: drawSize,
      overCapacity: playerCount > drawSize * 2,
    };
  }
  const preliminaryMatches = playerCount > halfRoundSize
    ? Math.max(Math.min(playerCount, drawSize) - halfRoundSize, 0)
    : 0;
  const directAdvancers = preliminaryMatches > 0
    ? Math.max(drawSize - playerCount, 0)
    : 0;
  return {
    drawSize,
    preliminaryMatches,
    directAdvancers,
    nextRoundSize: halfRoundSize,
    overCapacity: playerCount > drawSize * 2,
  };
}

interface AdminTournamentGroupsPageProps {
  setupMode?: boolean;
}

type AutoAssignMode = "random" | "rank";

interface GroupAssignmentPlanItem {
  participantId: number;
  groupId: number;
  /** Grupo actual cuando se reparte desde cero (mover en vez de asignar). */
  currentGroupId?: number | null;
}

/** Categoría a la vista cuando la dirección no trae ?division: la misma que
 * pondrá la barra de arriba (la primera activa por nombre), para no mostrar
 * una y saltar a otra al terminar de cargar la barra. Si todas están
 * desactivadas, la primera de la lista. */
function defaultDivisionId(divisions: readonly TournamentDivision[]): number | null {
  return firstActiveDivisionByName(divisions)?.id ?? divisions[0]?.id ?? null;
}

export default function AdminTournamentGroupsPage({
  setupMode = false,
}: AdminTournamentGroupsPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [formatConfig, setFormatConfig] = useState<TournamentFormatConfig | null>(null);
  const [groups, setGroups] = useState<TournamentGroup[]>([]);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);
  // Las preguntas son del torneo y la categoría a la vista: cambiar de uno u
  // otro (o salir) las retira y contestan «no».
  const { askConfirm, currentSignal } = useAbortableConfirm([tournamentId, selectedDivisionId]);
  // Sorteo público de grupos (Eduardo 2026-09-26): si la categoría ya se
  // sorteó, mover jugadores se permite con aviso (queda en el acta).
  const [groupDrawDone, setGroupDrawDone] = useState(false);
  const [groupsVersion, setGroupsVersion] = useState(0);
  const [unassigned, setUnassigned] = useState<TournamentParticipant[]>([]);
  // Series por equipos (Mini Copa 2026): pareja completa y con ambos pagos
  // para entrar al sorteo; las demás se ven con su motivo y no se reparten.
  const [drawEligibility, setDrawEligibility] = useState<Record<string, DrawEligibility>>({});
  const [staleRegistrations, setStaleRegistrations] = useState<Record<string, string>>({});
  const [removingStaleId, setRemovingStaleId] = useState<number | null>(null);
  const [history, setHistory] = useState<TournamentGroupMovementEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Sin selector de categoría propio (Eduardo 2026-10-05, torneo 69): el
  // grupo se crea en la categoría elegida arriba. Con un selector aparte se
  // podía crear en otra categoría sin verlo en la lista. (Los 9 rechazos 409
  // de ese día fueron al ASIGNAR jugadores a «GRUPO A», no al crear grupos;
  // el motivo no quedó en la bitácora: ver inSelectedCategory.)
  // Maqueta Grupos-Unificado (Eduardo 2026-10-15): «Crear grupos» con número
  // y jugadores por grupo; vacío = lo que diga el Formato / sin límite.
  const [groupCountInput, setGroupCountInput] = useState("");
  const [newGroupCapacity, setNewGroupCapacity] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const [pendingSearch, setPendingSearch] = useState("");
  // Sorteo público del torneo (casilla de Datos). null = aún no se sabe:
  // mientras, no se muestra la tarjeta del sorteo.
  const [publicDrawEnabled, setPublicDrawEnabled] = useState<boolean | null>(null);
  // Mientras el organizador no escriba, «Crear grupos» muestra lo que hay
  // (o lo que pide el Formato).
  const groupCountTouchedRef = useRef(false);

  const [selectedParticipantIds, setSelectedParticipantIds] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);
  // Rediseño «Asignar jugadores» (Eduardo 2026-10-14): cada grupo tiene su
  // botón «Asignar N aquí»; el reparto automático se ve antes de guardar.
  const [autoPreview, setAutoPreview] = useState<{ mode: AutoAssignMode; plan: GroupAssignmentPlanItem[] } | null>(null);
  const [lastAssigned, setLastAssigned] = useState<{ groupId: number; count: number } | null>(null);
  // Doble clic: el estado `assigning` tarda un render; el ref no.
  const assignBusyRef = useRef(false);
  const targetsRef = useRef<HTMLDivElement | null>(null);
  const [continuingToRounds, setContinuingToRounds] = useState(false);
  const [autoAssigningMode, setAutoAssigningMode] = useState<AutoAssignMode | null>(null);
  const [autoAssigningAll, setAutoAssigningAll] = useState(false);
  // Reparto desde cero (Eduardo 2026-09-08): tras el auto-assign de grupo
  // único, al crear un 2º grupo todos seguían en el primero y no había forma
  // de repartirlos sin quitarlos uno por uno.
  const [reshuffleAll, setReshuffleAll] = useState(false);

  const [moveTargets, setMoveTargets] = useState<Record<number, number | "">>({});
  const [moveReasons, setMoveReasons] = useState<Record<number, string>>({});
  const [removeReasons, setRemoveReasons] = useState<Record<number, string>>({});
  // Resultado de Mover / Quitar junto al renglón (Eduardo 2026-10-01: «cuando
  // muevo a algún jugador de grupo en este dropdown no sucede el cambio»): el
  // aviso general vive arriba de la página y no se ve con la lista abierta.
  const [rowFeedback, setRowFeedback] = useState<
    Record<number, { tone: "ok" | "error"; text: string }>
  >({});
  // Round Robin (Eduardo 2026-10-01): «Mover a» se sustituye por «Cambiar de
  // grupo…», que acomoda también los partidos. Candado y permiso por
  // categoría; vacío en categorías que no son de Round Robin.
  const [arrangementByDivision, setArrangementByDivision] = useState<Record<string, GroupArrangementCategory>>({});
  const [changeGroupFor, setChangeGroupFor] = useState<{ participantId: number; groupId: number } | null>(null);
  // «Reemplazar jugador» (Eduardo 2026-10-14): quién ya jugó algo (botón apagado).
  const [replaceFor, setReplaceFor] = useState<number | null>(null);
  const [playedIds, setPlayedIds] = useState<Set<number>>(new Set());
  // Rediseño Grupos (propuesta aprobada por Eduardo): modo lectura por
  // default; el editor de cada fila (ranks/mover/quitar) se abre con "⋯".
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [initialRankInputs, setInitialRankInputs] = useState<
    Record<number, string>
  >({});
  const [currentRankInputs, setCurrentRankInputs] = useState<
    Record<number, string>
  >({});
  const [savingRanks, setSavingRanks] = useState<Record<number, boolean>>({});
  const loadDataSeqRef = useRef(0);
  // Categorías de GRUPO ÚNICO: un solo intento de auto-asignación por scope
  // y por montaje — el endpoint es idempotente, pero sin este guard un fallo
  // del backend reintentaría en bucle.

  useEffect(() => {
    const rawDivisionId = searchParams.get("division");
    const urlDivisionId = rawDivisionId && Number.isInteger(Number(rawDivisionId)) ? Number(rawDivisionId) : null;
    // Sin ?division la pantalla usa la misma categoría que loadData (la que
    // pondrá la barra de arriba). Eduardo 2026-10-05 (revisión del torneo
    // 69): este efecto la regresaba a null, la carga la volvía a poner y así
    // sin fin, pidiendo la lista una y otra vez hasta que la barra ponía
    // ?division.
    const nextDivisionId = urlDivisionId ?? defaultDivisionId(divisions);
    if (nextDivisionId !== selectedDivisionId) {
      setSelectedDivisionId(nextDivisionId);
      setSelectedParticipantIds([]);
      setAutoPreview(null);
      setLastAssigned(null);
    }
  }, [searchParams, selectedDivisionId, divisions]);

  // Los nombres repetidos («Grupo 1» de Sub-10 y de Sub-12) llevan su
  // categoría donde se junten grupos de varias (p. ej. un torneo con grupos
  // sin categoría y el historial de movimientos, que es de todo el torneo).
  const repeatedGroupNames = useMemo(() => repeatedTournamentGroupNames(groups), [groups]);

  /** ¿El grupo (o jugador) es de la categoría elegida arriba? En un torneo
   * sin categorías, los que no tienen. Eduardo 2026-10-05 (torneo 69): el
   * servidor rechazó 9 asignaciones a «GRUPO A» (la bitácora no guarda el
   * motivo). Una sospecha: la pantalla ofrecía como destino grupos de otra
   * categoría (sin ?division, o con datos viejos de la categoría anterior).
   * Por eso los destinos («Asignar», «Repartir automático», «Mover a…»,
   * «Cambiar de grupo…») solo salen de aquí.
   * No cubre otra sospecha: una pestaña abierta horas mientras a un jugador
   * le cambian la categoría en Jugadores; aquí sigue con la de antes. A ese
   * lo detiene el servidor (dice su categoría y la del grupo) y la recarga
   * después del lote. */
  function inSelectedCategory(divisionId: number | null | undefined): boolean {
    if (divisions.length === 0) return divisionId == null;
    return selectedDivisionId != null && divisionId === selectedDivisionId;
  }

  /** Grupos activos de la categoría elegida: los únicos destinos posibles. */
  function destinationGroups(): TournamentGroup[] {
    return groups.filter((group) => group.is_active && inSelectedCategory(group.division_id));
  }

  const describeGroup = useCallback(
    (groupId: number | null): string => {
      if (groupId === null) return "—";
      const g = groups.find((item) => item.id === groupId);
      return g ? tournamentGroupLabel(g, repeatedGroupNames) : `Grupo #${groupId}`;
    },
    [groups, repeatedGroupNames],
  );

  const loadTournament = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
      setFormatConfig(response.data.format_config);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      }
    }
  }, [token, tournamentId]);

  const loadData = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    const requestSeq = ++loadDataSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const requestedDivisionId = (() => {
        const raw = searchParams.get("division");
        return raw && Number.isInteger(Number(raw)) ? Number(raw) : null;
      })();
      const divisionParams = requestedDivisionId ? { division_id: requestedDivisionId } : {};
      const [groupsResp, unassignedResp, historyResp, divisionsResp, arrangementResp] = await Promise.all([
        api.groups.list(token, tournamentId, divisionParams),
        api.groups.unassigned(token, tournamentId),
        api.groups.history(token, tournamentId),
        api.tournaments.listDivisions(token, tournamentId),
        // Solo trae categorías de Round Robin; si falla, la pantalla sigue
        // igual (el backend vuelve a validar al revisar el cambio).
        api.groups.arrangementStatus(token, tournamentId, divisionParams).catch(() => null),
      ]);
      if (requestSeq !== loadDataSeqRef.current) return;
      const nextDivisions = divisionsResp.data.divisions;
      const nextSelectedDivisionId = requestedDivisionId
        ? nextDivisions.find((division) => division.id === requestedDivisionId)?.id ?? null
        : defaultDivisionId(nextDivisions);
      if (nextSelectedDivisionId !== selectedDivisionId) setSelectedDivisionId(nextSelectedDivisionId);
      // Eduardo 2026-10-05 (revisión del torneo 69): sin ?division se pide la
      // lista de TODO el torneo, pero la pantalla se queda con una categoría
      // y sus jugadores sin grupo; podía ofrecer de destino los grupos de las
      // otras (el servidor los rechaza). Solo los de la categoría a la vista.
      setGroups(
        nextSelectedDivisionId != null
          ? groupsResp.data.groups.filter((group) => group.division_id === nextSelectedDivisionId)
          : groupsResp.data.groups,
      );
      setUnassigned(
        nextSelectedDivisionId
          ? unassignedResp.data.participants.filter((participant) => participant.division_id === nextSelectedDivisionId)
          : unassignedResp.data.participants,
      );
      setDrawEligibility(unassignedResp.data.draw_eligibility ?? {});
      setStaleRegistrations(unassignedResp.data.stale_registrations ?? {});
      setHistory(historyResp.data.history);
      setDivisions(nextDivisions);
      const nextArrangement: Record<string, GroupArrangementCategory> = {};
      for (const category of arrangementResp?.data.categories ?? []) {
        nextArrangement[arrangementKey(category.division_id)] = category;
      }
      setArrangementByDivision(nextArrangement);
    } catch (err) {
      // Latest-only también en el catch (re-gate #137): un reload de A que
      // rechaza tarde bajo B no escribe ni su error.
      if (requestSeq !== loadDataSeqRef.current) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los grupos.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (requestSeq === loadDataSeqRef.current) {
        setLoading(false);
        setGroupsVersion((version) => version + 1);
      }
    }
  }, [token, tournamentId, selectedDivisionId, searchParams]);

  // Después de guardar (crear, asignar, mover, quitar, ranks, sorteo…) se
  // recarga con el cargador del render MÁS NUEVO, no con el que el clic dejó
  // capturado: si mientras esperaban cambiaron la categoría arriba, el
  // capturado pediría la categoría de antes y, con su secuencia más nueva,
  // pintaría sus grupos bajo la categoría elegida (mismo arreglo que
  // Jugadores, #434).
  const loadDataRef = useRef(loadData);
  useLayoutEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);
  const reloadData = useCallback(() => loadDataRef.current(), []);

  // El estado del sorteo es por categoría: la tarjeta lo vuelve a informar.
  useEffect(() => {
    setGroupDrawDone(false);
  }, [selectedDivisionId]);

  // Tras el sorteo no se "reparte desde cero" (decisión 2: no se repite).
  useEffect(() => {
    if (groupDrawDone) setReshuffleAll(false);
  }, [groupDrawDone]);

  // Round Robin con partidos de grupo ya creados: «Repartir desde cero»
  // movería jugadores sin acomodar sus partidos (Eduardo 2026-10-01).
  const reshuffleBlockedByMatches = Object.values(arrangementByDivision).some((category) => category.has_matches);
  useEffect(() => {
    if (reshuffleBlockedByMatches) setReshuffleAll(false);
  }, [reshuffleBlockedByMatches]);

  // Al cambiar de categoría, «Crear grupos» vuelve a tomar sus valores.
  useEffect(() => {
    groupCountTouchedRef.current = false;
    setPendingSearch("");
  }, [selectedDivisionId]);

  useEffect(() => {
    if (groupCountTouchedRef.current) return;
    const active = groups.filter((group) => group.is_active);
    const division = divisions.find((item) => item.id === selectedDivisionId) ?? null;
    const count = active.length || formatGroupCount(division, formatConfig) || 0;
    setGroupCountInput(count > 0 ? String(count) : "");
    const capacity = active.find((group) => group.capacity != null && group.capacity > 0)?.capacity ?? null;
    setNewGroupCapacity(capacity != null ? String(capacity) : "");
  }, [groupsVersion, groups, divisions, selectedDivisionId, formatConfig]);

  // La vista previa del reparto vale para lo que se veía: si cambian la
  // selección, «desde cero» o los grupos (recarga), se descarta y se arma otra.
  useEffect(() => {
    setAutoPreview(null);
  }, [selectedParticipantIds, reshuffleAll, groupsVersion]);

  useEffect(() => {
    void loadTournament();
  }, [loadTournament]);

  // ¿El torneo tiene sorteo público? (casilla de Datos). Sin él no hay
  // tarjeta de sorteo: el reparto se hace aquí, sin acta ni aviso.
  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let alive = true;
    api.tournaments
      .getDrawCeremonyPlan(token, tournamentId)
      .then((response) => {
        if (alive) setPublicDrawEnabled(Boolean(response.data.plan?.enabled));
      })
      .catch(() => {
        if (alive) setPublicDrawEnabled(false);
      });
    return () => {
      alive = false;
    };
  }, [token, tournamentId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    // Unmount (re-gate #137): caduca cualquier loadData en vuelo — sus
    // success/catch/finally verifican requestSeq y ya no escriben
    // data/error/loading después del cleanup.
    return () => {
      loadDataSeqRef.current += 1;
    };
  }, []);

  // La auto-asignación de GRUPO ÚNICO ya NO corre al entrar (Eduardo
  // 2026-09-08: "que no entre en automático asignando a todos"): vive en
  // "Continuar a Draw" — si el admin no armó grupos y el Formato marca 1
  // grupo, ahí se crea el grupo único y se asigna a todos.

  /** Qué pasaría con los partidos (no escribe). Si falla, se sigue como
   * antes: el servidor vuelve a decidir al guardar. */
  async function previewMembership(params: MembershipChangePreviewParams): Promise<MembershipChangePreview | null> {
    if (!token) return null;
    const response = await settleWithin(api.groups.membershipPreview(token, tournamentId, params));
    return response?.data ?? null;
  }

  /** Grupo único: asigna a los que faltan. Si el Round Robin ya está
   * generado, el servidor no escribe nada y manda el aviso (qué se cancela,
   * qué se queda sin programar); se confirma y se repite. null = el
   * organizador dijo que no. */
  async function autoAssignSingleConfirmed(authToken: string) {
    const signal = currentSignal();
    try {
      return await api.groups.autoAssignSingle(authToken, tournamentId, { division_id: selectedDivisionId });
    } catch (err) {
      if (!(err instanceof ApiError) || err.code !== "rr_rebuild_confirm_required") throw err;
      const accepted = await askConfirm(err.message, { acceptLabel: "Sí, asignar y rehacer", tone: "danger", signal });
      if (!accepted || signal?.aborted) return null;
      return api.groups.autoAssignSingle(authToken, tournamentId, {
        division_id: selectedDivisionId,
        confirm_rebuild: true,
      });
    }
  }

  /** Grupos activos de la categoría en orden (sort_order y nombre). */
  function orderedDestinationGroups(): TournamentGroup[] {
    return destinationGroups().sort((a, b) =>
      a.sort_order !== b.sort_order ? a.sort_order - b.sort_order : a.name.localeCompare(b.name, "es-MX"),
    );
  }

  function parsedNewCapacity(): number | null | "invalid" {
    const raw = newGroupCapacity.trim();
    if (!raw) return null;
    const value = Number(raw);
    return Number.isInteger(value) && value >= 1 ? value : "invalid";
  }

  /** Crea grupos vacíos en la categoría a la vista (la de arriba). */
  async function createGroups(count: number): Promise<boolean> {
    if (!token || count <= 0) return false;
    if (divisions.length > 0 && selectedDivisionId == null) {
      setError("Elige arriba la categoría del grupo.");
      return false;
    }
    const capacity = parsedNewCapacity();
    if (capacity === "invalid") {
      setError("«Jugadores por grupo» debe ser un número entero de 1 en adelante, o vacío para sin límite.");
      return false;
    }
    const names = nextGroupNames(groups.map((group) => group.name), count);
    setSavingGroup(true);
    setMessage(null);
    setError(null);
    let created = 0;
    let failure: string | null = null;
    try {
      for (const name of names) {
        await api.groups.create(token, tournamentId, { name, division_id: selectedDivisionId, capacity, notes: null });
        created += 1;
      }
    } catch (err) {
      failure = err instanceof ApiError ? err.message || "No se pudo crear el grupo." : "Error de red. Intenta otra vez.";
    } finally {
      setSavingGroup(false);
    }
    // Recargar primero y avisar después: la carga limpia el aviso al empezar.
    await reloadData();
    if (failure) setError(failure);
    else setMessage(created === 1 ? `Grupo creado: ${names[0]}.` : `${created} grupos creados.`);
    return failure === null;
  }

  /** «Crear N grupos»: agrega los que faltan o quita grupos VACÍOS del final;
   * nunca saca a un jugador asignado. */
  async function handleSetGroupCount(target: number) {
    if (!token) return;
    const active = orderedDestinationGroups();
    if (target > active.length) {
      await createGroups(target - active.length);
      return;
    }
    const extra = active.length - target;
    if (extra <= 0) return;
    const removable: TournamentGroup[] = [];
    for (const group of [...active].reverse()) {
      if (removable.length === extra) break;
      if ((group.participants?.length ?? 0) > 0) break;
      removable.push(group);
    }
    if (removable.length < extra) {
      setError("Solo se quitan grupos vacíos del final: primero mueve a sus jugadores.");
      return;
    }
    const names = removable.map((group) => tournamentGroupLabel(group, repeatedGroupNames)).join(", ");
    if (!(await askConfirm(`¿Quitar ${extra === 1 ? "el grupo vacío" : `${extra} grupos vacíos`}: ${names}?`, { acceptLabel: "Sí, quitar", tone: "danger" }))) {
      return;
    }
    setSavingGroup(true);
    setMessage(null);
    setError(null);
    let failure: string | null = null;
    try {
      for (const group of removable) await api.groups.remove(token, tournamentId, group.id);
    } catch (err) {
      failure = err instanceof ApiError ? err.message || "No se pudo quitar el grupo." : "Error de red. Intenta otra vez.";
    } finally {
      setSavingGroup(false);
    }
    await reloadData();
    if (failure) setError(failure);
    else setMessage(extra === 1 ? "Grupo quitado." : `${extra} grupos quitados.`);
  }

  function toggleParticipantSelection(participantId: number) {
    setSelectedParticipantIds((prev) =>
      prev.includes(participantId)
        ? prev.filter((id) => id !== participantId)
        : [...prev, participantId],
    );
  }

  function selectAllUnassigned() {
    setSelectedParticipantIds(visiblePending().map((p) => p.id));
  }

  /** Sin grupo de la categoría, filtrados por la búsqueda. */
  function visiblePending(): TournamentParticipant[] {
    const query = foldText(pendingSearch.trim());
    if (!query) return unassigned;
    return unassigned.filter((p) => foldText(participantFullLabel(p)).includes(query));
  }

  function clearParticipantSelection() {
    setSelectedParticipantIds([]);
  }

  /** Asigna (o mueve) el plan. Devuelve cuántos entraron, o null si no se
   * hizo nada (otro lote en curso, plan vacío o el organizador dijo que no). */
  async function runGroupAssignments(
    assignments: GroupAssignmentPlanItem[],
    successMessage: string,
  ): Promise<number | null> {
    if (assignBusyRef.current) return null;
    assignBusyRef.current = true;
    try {
      return await runGroupAssignmentsOnce(assignments, successMessage);
    } finally {
      assignBusyRef.current = false;
    }
  }

  async function runGroupAssignmentsOnce(
    assignments: GroupAssignmentPlanItem[],
    successMessage: string,
  ): Promise<number | null> {
    if (!token) return null;
    const signal = currentSignal();
    if (assignments.length === 0) {
      setError("No hay jugadores por asignar.");
      return null;
    }
    // Eduardo 2026-10-13: con el Round Robin ya generado, asignar o mover
    // rehace los partidos del grupo y los deja sin programar. Antes de
    // confirmar se avisa (una revisión por grupo destino).
    const previewByGroup = new Map<number, GroupAssignmentPlanItem>();
    for (const item of assignments) {
      if (item.currentGroupId != null && item.currentGroupId === item.groupId) continue;
      if (!previewByGroup.has(item.groupId)) previewByGroup.set(item.groupId, item);
    }
    const previews = await Promise.all(
      [...previewByGroup.values()].map((item) =>
        previewMembership(
          item.currentGroupId != null
            ? { action: "move", participant_id: item.participantId, to_group_id: item.groupId }
            : { action: "assign", participant_id: item.participantId, to_group_id: item.groupId },
        ),
      ),
    );
    // Se salió del torneo o de la categoría mientras se revisaba: nada se asigna.
    if (signal?.aborted) {
      setAutoAssigningMode(null);
      return null;
    }
    const rebuildWarnings = previewWarnings(previews);
    if (rebuildWarnings.length > 0 || groupDrawDone) {
      const confirmText = [
        `¿Asignar ${assignments.length === 1 ? "1 jugador" : `${assignments.length} jugadores`}?`,
        ...rebuildWarnings,
        ...(groupDrawDone ? [POST_DRAW_CHANGE_NOTICE] : []),
      ].join("\n\n");
      const accepted = await askConfirm(confirmText, {
        acceptLabel: "Sí, continuar",
        tone: previews.some(membershipLosesSchedule) ? "danger" : "default",
        signal,
      });
      if (!accepted) {
        setAutoAssigningMode(null);
        return null;
      }
    }
    setMessage(null);
    setError(null);
    setAssigning(true);
    try {
      const failures: GroupAssignmentFailure[] = [];
      const rebuilds: Array<RrRebuildSummary | undefined> = [];
      for (const item of assignments) {
        try {
          if (item.currentGroupId != null && item.currentGroupId === item.groupId) continue;
          if (item.currentGroupId != null) {
            const moved = await api.groups.move(token, tournamentId, item.participantId, {
              to_group_id: item.groupId,
              reason: "Reparto desde cero",
            });
            rebuilds.push(moved.data?.rr_rebuild);
          } else {
            const assigned = await api.groups.assign(token, tournamentId, item.groupId, {
              participant_id: item.participantId,
            });
            rebuilds.push(assigned.data?.rr_rebuild);
          }
        } catch (err) {
          const participant =
            unassigned.find((p) => p.id === item.participantId)
            ?? assignedParticipantsInScope().find((row) => row.participant.id === item.participantId)?.participant;
          const label = participantLabel(participant ?? null);
          if (err instanceof ApiError) {
            const reason = typeof err.details.reason === "string" ? err.details.reason : null;
            failures.push({ label, message: err.message, reason });
          } else {
            failures.push({ label, message: "error de red" });
          }
        }
      }
      setSelectedParticipantIds([]);
      await reloadData();
      const rebuildNotice = mergedRrRebuildNotice(rebuilds);
      if (failures.length > 0) {
        // Un mismo motivo se dice una vez (torneo 69: 9 rechazos seguidos al
        // mismo grupo).
        setError(summarizeGroupAssignmentFailures(assignments.length, failures));
        if (rebuildNotice) setMessage(rebuildNotice);
      } else {
        setMessage(rebuildNotice ? `${successMessage} ${rebuildNotice}` : successMessage);
      }
      return assignments.length - failures.length;
    } finally {
      setAssigning(false);
      setAutoAssigningMode(null);
    }
  }

  /** «Asignar N aquí» de un grupo: los seleccionados entran a ese grupo. */
  async function handleAssignToGroup(groupId: number) {
    const target = destinationGroups().find((group) => group.id === groupId);
    if (!target) {
      setError("Elige un grupo de la categoría seleccionada arriba.");
      return;
    }
    // Solo jugadores de la categoría a la vista (defensa: datos viejos de la
    // categoría anterior si su carga falló).
    const inScope = new Set(unassigned.filter((p) => inSelectedCategory(p.division_id)).map((p) => p.id));
    const participantIds = selectedParticipantIds.filter((participantId) => inScope.has(participantId));
    if (participantIds.length === 0) {
      setError("Selecciona uno o varios jugadores sin grupo.");
      return;
    }
    const label = tournamentGroupLabel(target, repeatedGroupNames);
    setAutoPreview(null);
    setLastAssigned(null);
    const done = await runGroupAssignments(
      participantIds.map((participantId) => ({ participantId, groupId })),
      `${participantIds.length === 1 ? "1 asignado" : `${participantIds.length} asignados`} a ${label}.`,
    );
    if (done) setLastAssigned({ groupId, count: done });
  }

  /** Jugadores ya asignados en el scope con su grupo actual (reparto desde cero). */
  function assignedParticipantsInScope(): Array<{ participant: TournamentParticipant; groupId: number }> {
    const out: Array<{ participant: TournamentParticipant; groupId: number }> = [];
    for (const group of destinationGroups()) {
      for (const assignment of group.participants ?? []) {
        if (assignment.participant) out.push({ participant: assignment.participant, groupId: group.id });
      }
    }
    return out;
  }

  function eligibilityReason(participant: TournamentParticipant): string | null {
    const stale = staleRegistrations[String(participant.id)];
    if (stale) return stale;
    const entry = drawEligibility[String(participant.id)];
    if (!entry || entry.eligible) return null;
    if (!entry.complete) return "Pareja incompleta";
    return entry.unpaid.length > 0 ? `Sin pago: ${entry.unpaid.join(", ")}` : "Pago pendiente";
  }

  // Torneo 112: parejas cuyo integrante se borró en Inscripciones o con la
  // inscripción rechazada seguían «sin grupo» y bloqueaban la compuerta.
  // «Quitar del torneo» las da de baja (sale del padrón; lo jugado se queda).
  async function handleRemoveStale(participant: TournamentParticipant) {
    if (!token) return;
    const reason = staleRegistrations[String(participant.id)] ?? "Sin registro";
    const name = participantLabel(participant);
    if (!(await askConfirm(
      `¿Quitar a ${name} del torneo? ${reason}. Queda dado de baja: no se asigna a ningún grupo y sus partidos pendientes se cancelan (sin W/O). Los resultados ya jugados se conservan.`,
      { acceptLabel: "Sí, quitar del torneo", tone: "danger" },
    ))) return;
    setRemovingStaleId(participant.id);
    setError(null);
    setMessage(null);
    try {
      await api.participants.drop(token, tournamentId, participant.id, {
        reason: `${reason}: quitado del torneo desde Grupos`,
        pending_matches_action: "cancel",
      });
      setMessage(`${name} quitado del torneo.`);
      await reloadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo quitar del torneo." : "Error de red. Intenta otra vez.");
    } finally {
      setRemovingStaleId(null);
    }
  }

  function getAutoAssignParticipants() {
    const selected = new Set(selectedParticipantIds);
    const unassignedInScope = unassigned.filter((p) => inSelectedCategory(p.division_id));
    if (selected.size > 0) return unassignedInScope.filter((p) => selected.has(p.id));
    // Sin selección explícita, el reparto deja fuera a las parejas no elegibles.
    const eligibleUnassigned = unassignedInScope.filter((p) => eligibilityReason(p) === null);
    if (reshuffleAll) {
      return [...eligibleUnassigned, ...assignedParticipantsInScope().map((row) => row.participant)];
    }
    return [...eligibleUnassigned];
  }

  function rankValue(participant: TournamentParticipant): number {
    // Manda el SEED (paso Ranking); la posición viva no es seed.
    return participant.initial_rank ?? participant.tournament_rank ?? Number.MAX_SAFE_INTEGER;
  }

  function buildAutoAssignPlan(mode: AutoAssignMode): GroupAssignmentPlanItem[] {
    const activeGroups = destinationGroups()
      .sort((a, b) => {
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return a.name.localeCompare(b.name, "es-MX");
      });
    if (activeGroups.length === 0) return [];

    const reshuffle = reshuffleAll && selectedParticipantIds.length === 0;
    const currentGroupByParticipant = new Map<number, number>();
    assignedParticipantsInScope().forEach((row) => currentGroupByParticipant.set(row.participant.id, row.groupId));
    const counts: Record<number, number> = {};
    activeGroups.forEach((g) => {
      counts[g.id] = reshuffle ? 0 : (g.participants?.length ?? g.active_participant_count ?? 0);
    });

    const source = getAutoAssignParticipants();
    // Eduardo 2026-09-26 (misma regla que el sorteo): sembrados por seed y
    // S/R barajados; en "Por rank" los S/R van al grupo más ligero al azar.
    const seededSource = [...source]
      .filter((participant) => rankValue(participant) !== Number.MAX_SAFE_INTEGER)
      .sort((a, b) => {
        const rankA = rankValue(a);
        const rankB = rankValue(b);
        if (rankA !== rankB) return rankA - rankB;
        return participantLabel(a).localeCompare(participantLabel(b), "es-MX");
      });
    const unseededSource = shuffled(source.filter((participant) => rankValue(participant) === Number.MAX_SAFE_INTEGER));
    const participants = mode === "rank" ? [...seededSource, ...unseededSource] : shuffled(source);

    // Serpentina cuando el reparto arranca de grupos vacíos (primera vez o
    // "Repartir desde cero"); completar grupos con gente va al más ligero.
    const startsEmpty = activeGroups.every((group) => counts[group.id] === 0);
    const slots = planGroupSlots(
      activeGroups,
      counts,
      participants.map((participant) => ({
        id: participant.id,
        seeded: mode === "rank" && rankValue(participant) !== Number.MAX_SAFE_INTEGER,
      })),
      { snakeSeeded: mode === "rank" && (reshuffle || startsEmpty) },
    );
    return slots.map((slot) => ({
      participantId: slot.participantId,
      groupId: slot.groupId,
      currentGroupId: currentGroupByParticipant.get(slot.participantId) ?? null,
    }));
  }

  // Botón dentro del aviso "Faltan N por asignar" (Cowork 2026-09-20): con un
  // solo grupo entran todos (el backend crea el grupo si no existe); con varios
  // grupos usa el reparto por ranking que ya existe.
  async function handleAssignAllAutomatically() {
    if (!token) return;
    if (!singleGroupScopeNotice) {
      await handleAutoAssign("rank");
      return;
    }
    setAutoAssigningAll(true);
    setError(null);
    setMessage(null);
    try {
      const auto = await autoAssignSingleConfirmed(token);
      if (!auto) return;
      const skipped = auto.data.skipped_ineligible ?? [];
      if (skipped.length > 0) {
        setError(`Fuera del sorteo: ${skipped.map((item) => `${item.label} (${item.reason})`).join("; ")}.`);
      }
      setMessage(
        withRrRebuildNotice(
          `${auto.data.assigned_count} jugador(es) asignados a ${auto.data.group.name}${auto.data.group_created ? " (grupo creado en automático)" : ""}.`,
          auto.data.rr_rebuild,
        ),
      );
      await reloadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo asignar en automático.");
    } finally {
      setAutoAssigningAll(false);
    }
  }

  async function handleAutoAssign(mode: AutoAssignMode) {
    const sourceCount = getAutoAssignParticipants().length;
    if (sourceCount === 0) {
      setError(reshuffleAll ? "No hay jugadores en esta categoría para repartir." : "No hay jugadores sin grupo para auto-asignar.");
      return;
    }
    const plan = buildAutoAssignPlan(mode);
    if (plan.length === 0) {
      setError("No hay grupos activos con espacio para asignar jugadores.");
      return;
    }
    // Primero se enseña (Eduardo 2026-10-14): nada se guarda hasta «Aplicar».
    setError(null);
    setLastAssigned(null);
    setAutoPreview({ mode, plan });
    // La vista previa se dibuja dentro de los grupos azules.
    window.requestAnimationFrame?.(() => {
      targetsRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    });
  }

  /** «Aplicar reparto»: manda EXACTAMENTE el plan que se vio. */
  async function applyAutoPreview() {
    if (!autoPreview) return;
    const { mode, plan } = autoPreview;
    setAutoAssigningMode(mode);
    const done = await runGroupAssignments(
      plan,
      mode === "rank"
        ? `${plan.length} jugador(es) auto-asignado(s) por rank.`
        : `${plan.length} jugador(es) auto-asignado(s) de forma random balanceada.`,
    );
    if (done !== null) setAutoPreview(null);
  }

  /** Vista previa dentro de cada grupo: quién entra (y de dónde viene) y
   * quién sale cuando se reparte desde cero. Nada se guarda todavía. */
  function previewByGroup(plan: GroupAssignmentPlanItem[]): {
    incoming: Map<number, Array<{ participantId: number; name: string; fromLabel: string | null }>>;
    leaving: Set<number>;
  } {
    const names = new Map<number, string>();
    unassigned.forEach((p) => names.set(p.id, participantLabel(p)));
    assignedParticipantsInScope().forEach((row) => names.set(row.participant.id, participantLabel(row.participant)));
    const incoming = new Map<number, Array<{ participantId: number; name: string; fromLabel: string | null }>>();
    const leaving = new Set<number>();
    for (const item of plan) {
      if (item.currentGroupId != null && item.currentGroupId === item.groupId) continue;
      if (item.currentGroupId != null) leaving.add(item.participantId);
      const list = incoming.get(item.groupId) ?? [];
      list.push({
        participantId: item.participantId,
        name: names.get(item.participantId) ?? `#${item.participantId}`,
        fromLabel: item.currentGroupId != null ? describeGroup(item.currentGroupId) : null,
      });
      incoming.set(item.groupId, list);
    }
    return { incoming, leaving };
  }

  /** «Repartir desde cero» (menú ⋯): incluye a los ya asignados. */
  async function handleStartReshuffle() {
    if (
      !(await askConfirm(
        "¿Repartir desde cero? Se arma un reparto nuevo con TODOS los jugadores de la categoría (también los ya asignados). Primero ves la vista previa; nada cambia hasta «Confirmar reparto».",
        { acceptLabel: "Sí, repartir desde cero" },
      ))
    ) {
      return;
    }
    setSelectedParticipantIds([]);
    setReshuffleAll(true);
  }

  async function handleMove(participantId: number) {
    if (!token) return;
    const signal = currentSignal();
    const target = moveTargets[participantId];
    if (!target) {
      setError("Selecciona un grupo destino.");
      setRowFeedback((prev) => ({ ...prev, [participantId]: { tone: "error", text: "Selecciona un grupo destino." } }));
      return;
    }
    const preview = await previewMembership({ action: "move", participant_id: participantId, to_group_id: Number(target) });
    if (signal?.aborted) return;
    if (
      (groupDrawDone || previewWarnings([preview]).length > 0)
      && !(await askConfirm(
        membershipConfirmText("¿Mover al jugador?", preview, groupDrawDone ? POST_DRAW_CHANGE_NOTICE : null),
        { acceptLabel: "Sí, mover", tone: membershipLosesSchedule(preview) ? "danger" : "default", signal },
      ))
    ) {
      return;
    }
    setMessage(null);
    setError(null);
    try {
      const moved = await api.groups.move(token, tournamentId, participantId, {
        to_group_id: Number(target),
        reason: (moveReasons[participantId] || "").trim() || null,
      });
      setMoveTargets((prev) => ({ ...prev, [participantId]: "" }));
      setMoveReasons((prev) => ({ ...prev, [participantId]: "" }));
      setMessage(withRrRebuildNotice("Jugador movido.", moved.data?.rr_rebuild));
      setRowFeedback((prev) => ({ ...prev, [participantId]: { tone: "ok", text: "Jugador movido." } }));
      await reloadData();
    } catch (err) {
      const text =
        err instanceof ApiError
          ? err.message || "No se pudo mover al jugador."
          : "Error de red. Intenta otra vez.";
      setError(text);
      setRowFeedback((prev) => ({ ...prev, [participantId]: { tone: "error", text } }));
    }
  }

  /** Formato efectivo de la categoría del grupo (la categoría manda; sin
   * formato propio, el del torneo). */
  function isRoundRobinGroup(group: TournamentGroup): boolean {
    if (arrangementByDivision[arrangementKey(group.division_id)]) return true;
    const division = group.division_id != null ? divisions.find((item) => item.id === group.division_id) : null;
    const formatType = division?.format_type ?? formatConfig?.format_type ?? tournament?.tournament_type ?? null;
    return formatType === "round_robin";
  }

  /** Por qué no se puede usar «Cambiar de grupo…» en esa categoría (o null). */
  function changeGroupBlockedReason(group: TournamentGroup): string | null {
    const status = arrangementByDivision[arrangementKey(group.division_id)];
    if (!status) return null;
    if (status.locked) return status.message || ARRANGE_LOCK_FALLBACK;
    if (!status.can_edit) return "Tu perfil no permite acomodar grupos.";
    return null;
  }

  /** Categoría de Round Robin que ya empezó: «Cambiar de grupo…» queda
   * bloqueado, pero quien todavía no tiene partidos de grupo (p. ej. un
   * jugador tardío que quedó en el grupo equivocado) se sigue moviendo con
   * el «Mover a» de siempre: no hay partidos que acomodar. El backend vuelve
   * a revisarlo (`group_change_requires_plan`). */
  function plainMoveAllowedInStartedCategory(group: TournamentGroup, participantId: number): boolean {
    const status = arrangementByDivision[arrangementKey(group.division_id)];
    if (!status?.locked || status.reason === "tournament_closed") return false;
    return !(status.participants_with_group_matches ?? []).includes(participantId);
  }

  /** Grupos activos de la misma categoría (destinos posibles del cambio), y
   * esa categoría es la elegida arriba. */
  function sameCategoryTargets(group: TournamentGroup): TournamentGroup[] {
    return destinationGroups().filter(
      (candidate) =>
        candidate.id !== group.id
        && (candidate.division_id ?? null) === (group.division_id ?? null),
    );
  }

  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let alive = true;
    api.groups
      .replacePlayed(token, tournamentId)
      .then((response) => {
        if (alive) setPlayedIds(new Set(response.data.participant_ids));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [token, tournamentId, groups]);

  async function handleChangeGroupDone(participantId: number, notice: string | null = null) {
    setChangeGroupFor(null);
    setError(null);
    setMessage(notice ? `Cambio de grupo guardado. ${notice}` : "Cambio de grupo guardado.");
    setRowFeedback((prev) => ({ ...prev, [participantId]: { tone: "ok", text: "Cambio de grupo guardado." } }));
    await reloadData();
  }

  function renderChangeGroupDialog(target: { participantId: number; groupId: number }) {
    if (!token) return null;
    const source = groups.find((group) => group.id === target.groupId);
    if (!source) return null;
    const assignment = (source.participants ?? []).find((item) => item.participant_id === target.participantId);
    const targetGroups: RoundRobinChangeGroupTarget[] = sameCategoryTargets(source).map((candidate) => {
      const ordered = sortGroupAssignments(candidate);
      const namesById = new Map<number, string>();
      for (const item of ordered) namesById.set(item.participant_id, participantFullLabel(item.participant));
      // Miembros en el orden de la hoja: el del backend o el de esta tarjeta.
      const order = initialArrangeDraft(
        candidate,
        ordered.map((item) => item.participant_id),
        new Set(),
      );
      return {
        id: candidate.id,
        name: tournamentGroupLabel(candidate, repeatedGroupNames),
        members: order.map((id) => ({ id, name: namesById.get(id) ?? `#${id}` })),
      };
    });
    return (
      <RoundRobinChangeGroupDialog
        token={token}
        tournamentId={tournamentId}
        participant={{ id: target.participantId, name: participantFullLabel(assignment?.participant ?? null) }}
        fromGroup={{ id: source.id, name: tournamentGroupLabel(source, repeatedGroupNames) }}
        targetGroups={targetGroups}
        onDone={({ notice }) => void handleChangeGroupDone(target.participantId, notice)}
        onClose={() => setChangeGroupFor(null)}
      />
    );
  }

  async function handleRemove(participantId: number) {
    if (!token) return;
    const signal = currentSignal();
    // Eduardo 2026-10-13: sus partidos pendientes del grupo se cancelan y el
    // grupo se rehace sin él (sin programar); la confirmación lo avisa.
    const preview = await previewMembership({ action: "remove", participant_id: participantId });
    if (
      !(await askConfirm(
        membershipConfirmText(
          "¿Quitar a este jugador del grupo?",
          preview,
          groupDrawDone ? POST_DRAW_CHANGE_NOTICE : null,
        ),
        { acceptLabel: "Sí, quitar del grupo", tone: membershipLosesSchedule(preview) ? "danger" : "default", signal },
      ))
    ) {
      return;
    }
    setMessage(null);
    setError(null);
    try {
      const removed = await api.groups.removeParticipant(token, tournamentId, participantId, {
        reason: (removeReasons[participantId] || "").trim() || null,
      });
      setRemoveReasons((prev) => ({ ...prev, [participantId]: "" }));
      setMessage(withRrRebuildNotice("Jugador removido del grupo.", removed.data?.rr_rebuild));
      setRowFeedback((prev) => ({ ...prev, [participantId]: { tone: "ok", text: "Jugador removido del grupo." } }));
      await reloadData();
    } catch (err) {
      const text =
        err instanceof ApiError
          ? err.message || "No se pudo remover al jugador."
          : "Error de red. Intenta otra vez.";
      setError(text);
      setRowFeedback((prev) => ({ ...prev, [participantId]: { tone: "error", text } }));
    }
  }

  function parseRankValue(raw: string): number | null | "invalid" {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    if (Number.isNaN(value) || !Number.isFinite(value) || value < 1) {
      return "invalid";
    }
    return Math.trunc(value);
  }

  function storedRankValue(value: TournamentParticipant["initial_rank"]): number | null {
    if (value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }

  async function handleSaveRanks(participant: TournamentParticipant) {
    if (!token) return;
    const rawInitial =
      initialRankInputs[participant.id] ??
      (participant.initial_rank != null
        ? String(participant.initial_rank)
        : "");
    const rawCurrent =
      currentRankInputs[participant.id] ??
      (participant.current_rank != null
        ? String(participant.current_rank)
        : "");
    const parsedInitial = parseRankValue(rawInitial);
    const parsedCurrent = parseRankValue(rawCurrent);
    if (parsedInitial === "invalid" || parsedCurrent === "invalid") {
      setError("Los rangos deben ser números mayores a 0.");
      setMessage(null);
      return;
    }
    const payload: { initial_rank?: number | null; current_rank?: number | null } = {};
    if (parsedInitial !== storedRankValue(participant.initial_rank)) {
      payload.initial_rank = parsedInitial;
    }
    if (parsedCurrent !== storedRankValue(participant.current_rank)) {
      payload.current_rank = parsedCurrent;
    }
    if (Object.keys(payload).length === 0) {
      setError(null);
      setMessage("No hay cambios de ranking.");
      return;
    }
    setMessage(null);
    setError(null);
    setSavingRanks((prev) => ({ ...prev, [participant.id]: true }));
    try {
      await api.participants.update(token, tournamentId, participant.id, payload);
      setInitialRankInputs((prev) => {
        const next = { ...prev };
        delete next[participant.id];
        return next;
      });
      setCurrentRankInputs((prev) => {
        const next = { ...prev };
        delete next[participant.id];
        return next;
      });
      setMessage("Ranking del jugador actualizado.");
      await reloadData();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo actualizar el ranking.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSavingRanks((prev) => ({ ...prev, [participant.id]: false }));
    }
  }

  async function handleGroupEdited(text: string) {
    setError(null);
    setMessage(text);
    await reloadData();
  }

  async function handleDeactivateGroup(groupId: number) {
    if (!token) return;
    const signal = currentSignal();
    const deactivateMessage = "¿Desactivar este grupo? Sus jugadores saldrán del grupo y quedarán libres para reasignarlos.";
    // Sus partidos pendientes se cancelan (Eduardo 2026-10-13).
    const preview = await previewMembership({ action: "deactivate_group", group_id: groupId });
    if (
      !(await askConfirm(membershipConfirmText(deactivateMessage, preview, groupDrawDone ? POST_DRAW_CHANGE_NOTICE : null), {
        acceptLabel: "Sí, desactivar grupo",
        tone: membershipLosesSchedule(preview) ? "danger" : "default",
        signal,
      }))
    ) {
      return;
    }
    setMessage(null);
    setError(null);
    try {
      const deactivated = await api.groups.deactivate(token, tournamentId, groupId);
      setMessage(withRrRebuildNotice("Grupo desactivado.", deactivated.data?.rr_rebuild));
      await reloadData();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo desactivar el grupo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    }
  }

  async function handleReactivateGroup(groupId: number) {
    if (!token) return;
    // Eduardo 2026-09-24: que el admin entienda que reactivar = grupo vacío.
    const reactivateMessage = "¿Reactivar este grupo? Se reactiva vacío: los jugadores que aún aparezcan dentro salen del grupo y podrás volver a asignarlos.";
    if (
      !(await askConfirm(groupDrawDone ? `${reactivateMessage}\n\n${POST_DRAW_CHANGE_NOTICE}` : reactivateMessage, {
        acceptLabel: "Sí, reactivar vacío",
      }))
    ) {
      return;
    }
    setMessage(null);
    setError(null);
    try {
      await api.groups.reactivate(token, tournamentId, groupId);
      setMessage("Grupo reactivado (vacío): asigna a sus jugadores.");
      await reloadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo reactivar el grupo." : "Error de red. Intenta otra vez.");
    }
  }

  async function handleDeleteGroup(groupId: number) {
    if (!token) return;
    if (!(await askConfirm("¿Eliminar este grupo? Esta acción no se puede deshacer.", { acceptLabel: "Sí, eliminar grupo", tone: "danger" }))) {
      return;
    }
    setMessage(null);
    setError(null);
    try {
      await api.groups.remove(token, tournamentId, groupId);
      setMessage("Grupo eliminado.");
      await reloadData();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo eliminar el grupo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    }
  }

  async function handleContinueToRounds() {
    if (!token || Number.isNaN(tournamentId)) return;
    setContinuingToRounds(true);
    setMessage(null);
    setError(null);
    try {
      const response = await api.tournaments.get(token, tournamentId);
      const status = response.data.setup_status;
      setTournament(response.data.tournament);

      const scopedParticipantsCount = selectedDivisionId ? localParticipantsCount : (status.counts.participants ?? 0);
      const scopedGroupsCount = selectedDivisionId ? totalGroups : (status.counts.groups ?? 0);
      const scopedAssignedCount = selectedDivisionId ? assignedCount : (status.counts.group_assignments ?? 0);
      if (scopedParticipantsCount === 0) {
        navigate(`/admin/tournaments/${tournamentId}/setup/players${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`, { replace: true });
        return;
      }
      const scopeDivision = divisions.find((item) => item.id === selectedDivisionId) ?? null;
      const singleGroupScope = isSingleGroupScope(scopeDivision, formatConfig, response.data.tournament.tournament_type, scopedGroupsCount);
      if (
        singleGroupScope
        && scopedGroupsCount <= 1
        && scopedAssignedCount < scopedParticipantsCount
      ) {
        // Formato de 1 grupo y el admin no armó grupos: el grupo único se
        // crea aquí y se asigna a todos (idempotente; reutiliza el único
        // grupo activo si ya existe).
        const auto = await autoAssignSingleConfirmed(token);
        if (!auto) return;
        const skipped = auto.data.skipped_ineligible ?? [];
        if (skipped.length > 0) {
          setError(`Fuera del sorteo: ${skipped.map((item) => `${item.label} (${item.reason})`).join("; ")}.`);
        }
        const rebuildNotice = rrRebuildNotice(auto.data.rr_rebuild);
        if (rebuildNotice) {
          // Se rehicieron partidos: el organizador lo lee aquí antes de seguir.
          await reloadData();
          setMessage(`Grupo único: ${auto.data.assigned_count} jugador(es) asignados a ${auto.data.group.name}. ${rebuildNotice}`);
          return;
        }
        if (auto.data.assigned_count > 0) {
          setMessage(`Grupo único: ${auto.data.assigned_count} jugador(es) asignados a ${auto.data.group.name}.`);
        }
        navigate(`/admin/tournaments/${tournamentId}/setup/rounds${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`);
        return;
      }
      // Recargar primero y avisar después: la carga limpia el error al
      // empezar y el aviso se perdía.
      if (scopedGroupsCount === 0) {
        await reloadData();
        setError("Antes de continuar, crea al menos un grupo para esta categoría (o usa Auto-asignar).");
        return;
      }
      if (scopedAssignedCount < scopedParticipantsCount) {
        await reloadData();
        setError("Antes de continuar, asigna todos los jugadores de esta categoría a un grupo.");
        return;
      }
      navigate(`/admin/tournaments/${tournamentId}/setup/rounds${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo continuar a Draw.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setContinuingToRounds(false);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={8} tournamentStatus={tournament?.status} />}
        <section className="card">
          <p className="form-error">ID de torneo inválido.</p>
        </section>
      </>
    );
  }

  // Candado por categoría: sin formato guardado no se opera Grupos.
  const guardDivision = divisions.find((division) => division.id === selectedDivisionId) ?? null;
  if (guardDivision && divisionNeedsFormat(guardDivision)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={8} tournamentStatus={tournament?.status} />}
        <DivisionFormatGuardCard tournamentId={tournamentId} division={guardDivision} stepLabel="Grupos" />
      </>
    );
  }

  const totalGroups = groups.length;
  const activeGroupCount = groups.filter((g) => g.is_active).length;
  const assignedCount = groups.reduce(
    (acc, g) => acc + (g.participants?.length ?? 0),
    0,
  );
  const unassignedCount = unassigned.length;
  const staleInScope = unassigned.filter((p) => staleRegistrations[String(p.id)] && inSelectedCategory(p.division_id));
  const totalCapacity = groups.reduce(
    (acc, g) => acc + (g.capacity ?? 0),
    0,
  );
  const localParticipantsCount = assignedCount + unassignedCount;
  const singleGroupScopeNotice = isSingleGroupScope(
    divisions.find((item) => item.id === selectedDivisionId) ?? null,
    formatConfig,
    tournament?.tournament_type,
    totalGroups,
  );
  const canContinueToRounds =
    localParticipantsCount > 0
    && ((totalGroups > 0 && unassignedCount === 0) || (singleGroupScopeNotice && totalGroups <= 1));
  const hasCapacity = totalCapacity > 0;
  const capacityUsedPct = hasCapacity
    ? Math.min(100, Math.round((assignedCount / totalCapacity) * 100))
    : 0;
  const setupComplete = unassignedCount === 0 && totalGroups > 0 && !loading;
  const activeGroupsForAssignment = destinationGroups();
  const isElimination = tournament?.tournament_type === "elimination";
  const configuredBracketSize = isEliminationFormatConfig(formatConfig)
    ? formatConfig.config_json.bracket_size
    : null;
  const elimination = isElimination
    ? eliminationPlan(localParticipantsCount, configuredBracketSize)
    : { drawSize: 0, preliminaryMatches: 0, directAdvancers: 0, nextRoundSize: 0, overCapacity: false };
  const estimatedDrawSize = elimination.drawSize;
  const selectedCount = selectedParticipantIds.length;
  const isDoublesTournament = tournament?.play_mode === "doubles";
  const participantPlural = isDoublesTournament ? "parejas" : "jugadores";
  const participantPluralTitle = isDoublesTournament ? "Parejas" : "Jugadores";
  const assignedInScopeCount = activeGroupsForAssignment
    .reduce((sum, g) => sum + (g.participants?.length ?? g.active_participant_count ?? 0), 0);
  const autoAssignScopeLabel = selectedCount > 0
    ? `${selectedCount} seleccionado(s)`
    : reshuffleAll
      ? `${unassignedCount + assignedInScopeCount} en la categoría (reparto desde cero)`
      : `${unassignedCount} sin grupo`;
  const pendingVisible = visiblePending();
  // Solo los grupos de la categoría a la vista (si su carga falló, no se
  // quedan los de la anterior).
  const visibleGroups = groups.filter((group) => inSelectedCategory(group.division_id));
  const defaultGroupCount = activeGroupsForAssignment.length || formatGroupCount(guardDivision, formatConfig) || 2;
  const parsedGroupCount = groupCountInput.trim() ? Number(groupCountInput) : defaultGroupCount;
  const targetGroupCount = Number.isInteger(parsedGroupCount) && parsedGroupCount >= 1 && parsedGroupCount <= 64 ? parsedGroupCount : null;
  const preview = autoPreview ? previewByGroup(autoPreview.plan) : null;
  const showPublicDraw = publicDrawEnabled === true;
  // Todos asignados: la lista y el reparto se pliegan (maqueta, variante
  // «todos asignados»); «Repartir desde cero» queda en el menú ⋯.
  const allAssignedFolded = unassignedCount === 0 && totalGroups > 0 && !reshuffleAll && !loading;
  const drawCard = token && showPublicDraw ? (
    <GroupDrawCard
      token={token}
      tournamentId={tournamentId}
      divisionId={selectedDivisionId}
      divisionName={divisions.find((division) => division.id === selectedDivisionId)?.name ?? null}
      refreshKey={groupsVersion}
      onDrawn={reloadData}
      onStateChange={setGroupDrawDone}
    />
  ) : null;
  const autoAssignDisabled = assigning || (reshuffleAll && selectedCount === 0
    ? unassignedCount + assignedInScopeCount === 0
    : unassignedCount === 0);

  return (
    <div className={setupMode ? "wiz-step wiz-step--grupos" : "grp-page"}>
      {setupMode && (
        <TournamentSetupProgress
          current={8}
          tournamentStatus={tournament?.status}
          hero={(
            <section className="wiz-hero wiz-hero--navy grp-hero">
              <div className="grp-hero__top">
                <div>
                  <p className="eyebrow">{`Paso 8 de 10 · ${guardDivision?.name ?? tournament?.name ?? ""}`}</p>
                  <h1 className="hint-label">Grupos<GuideHelpButton /></h1>
                  <p className="wiz-hero__lead">1 Crea los grupos · 2 Asigna a los jugadores. Una sola vista de grupos.</p>
                </div>
                <span className={`grp-hero__chip${setupComplete ? " is-ok" : ""}`}>
                  {setupComplete ? "✓ Todos asignados" : unassignedCount > 0 ? `Faltan ${unassignedCount} por asignar` : "Sin grupos todavía"}
                </span>
              </div>
            <div className="group-dashboard__stats">
              <div className="group-dashboard__stat">
                <span className="group-dashboard__stat-label">Grupos</span>
                <span className="group-dashboard__stat-value">{totalGroups}</span>
                {totalGroups > 0 && activeGroupCount !== totalGroups && (
                  <span className="group-dashboard__stat-meta">
                    {activeGroupCount} activos
                  </span>
                )}
              </div>
              <div className="group-dashboard__stat">
                <span className="group-dashboard__stat-label">Asignados</span>
                <span className="group-dashboard__stat-value">{assignedCount}</span>
              </div>
              <div
                className={
                  "group-dashboard__stat" +
                  (unassignedCount > 0
                    ? " group-dashboard__stat--alert"
                    : totalGroups > 0
                      ? " group-dashboard__stat--ok"
                      : "")
                }
              >
                <span className="group-dashboard__stat-label">Sin grupo</span>
                <span className="group-dashboard__stat-value">
                  {unassignedCount}
                </span>
              </div>
              {hasCapacity && (
                <div className="group-dashboard__stat group-dashboard__stat--wide">
                  <span className="group-dashboard__stat-label">Capacidad</span>
                  <span className="group-dashboard__stat-value">
                    {assignedCount}
                    <span className="group-dashboard__stat-suffix">
                      /{totalCapacity}
                    </span>
                  </span>
                  <div
                    className="group-dashboard__progress"
                    role="progressbar"
                    aria-valuenow={capacityUsedPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="group-dashboard__progress-fill"
                      style={{ width: `${capacityUsedPct}%` }}
                    />
                  </div>
                </div>
              )}
              {isElimination && estimatedDrawSize > 0 && (
                <div className="group-dashboard__stat">
                  <span className="group-dashboard__stat-label">Bracket configurado</span>
                  <span className="group-dashboard__stat-value">Ronda {estimatedDrawSize}</span>
                  <span className="group-dashboard__stat-meta">
                    {elimination.overCapacity
                      ? `${localParticipantsCount} jugadores exceden la capacidad; ajusta Formato`
                      : elimination.preliminaryMatches > 0
                        ? `${elimination.preliminaryMatches} preliminar · ${elimination.directAdvancers} pasan directo a Ronda ${elimination.nextRoundSize}`
                        : "Sin ronda preliminar"}
                  </span>
                </div>
              )}
            </div>
            </section>
          )}
        />
      )}
      {setupMode && (
      <div className="grp-notices">
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
        {setupComplete ? (
          <p className="group-dashboard__hint group-dashboard__hint--ok">
            Todos los jugadores inscritos están asignados a un grupo.
            {isElimination && estimatedDrawSize > 0
              ? elimination.overCapacity
                ? ` El bracket configurado en Formato es Ronda ${estimatedDrawSize} y no alcanza para ${localParticipantsCount} jugador(es). Regresa a Formato antes de armar.`
                : elimination.preliminaryMatches > 0
                  ? ` Siguiente: revisar el Draw de Ronda ${estimatedDrawSize}: ${elimination.preliminaryMatches} partido(s) en Preliminar y ${elimination.directAdvancers} jugador(es) pasan directo a Ronda ${elimination.nextRoundSize}.`
                  : ` Siguiente: revisar el Draw de Ronda ${estimatedDrawSize}; no hay ronda preliminar.`
              : ""}
          </p>
        ) : unassignedCount > 0 ? (
          <p className="group-dashboard__hint group-dashboard__hint--alert">
            Faltan <strong>{unassignedCount}</strong> jugador(es) por asignar.{" "}
            {singleGroupScopeNotice || totalGroups > 0 ? (
              <button
                type="button"
                className="btn btn--small"
                disabled={autoAssigningAll || autoAssigningMode !== null}
                onClick={() => void handleAssignAllAutomatically()}
              >
                {autoAssigningAll ? "Asignando…" : "Asignarlos en automático"}
              </button>
            ) : (
              "Crea al menos un grupo y asígnalos."
            )}
          </p>
        ) : null}
      </div>
      )}
      {!setupMode && (
      <section className="card setup-call-card setup-wizard-card group-dashboard">
        <div className="setup-call-card__heading"><span>01</span><div><h2><span className="hint-label">Dashboard de asignación<GuideHelpButton /></span></h2><p>Indicadores para saber si el torneo está listo para el Draw.</p></div></div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
        <div className="group-dashboard__stats">
          <div className="group-dashboard__stat">
            <span className="group-dashboard__stat-label">Grupos</span>
            <span className="group-dashboard__stat-value">{totalGroups}</span>
            {totalGroups > 0 && activeGroupCount !== totalGroups && (
              <span className="group-dashboard__stat-meta">
                {activeGroupCount} activos
              </span>
            )}
          </div>
          <div className="group-dashboard__stat">
            <span className="group-dashboard__stat-label">Asignados</span>
            <span className="group-dashboard__stat-value">{assignedCount}</span>
          </div>
          <div
            className={
              "group-dashboard__stat" +
              (unassignedCount > 0
                ? " group-dashboard__stat--alert"
                : totalGroups > 0
                  ? " group-dashboard__stat--ok"
                  : "")
            }
          >
            <span className="group-dashboard__stat-label">Sin grupo</span>
            <span className="group-dashboard__stat-value">
              {unassignedCount}
            </span>
          </div>
          {hasCapacity && (
            <div className="group-dashboard__stat group-dashboard__stat--wide">
              <span className="group-dashboard__stat-label">Capacidad</span>
              <span className="group-dashboard__stat-value">
                {assignedCount}
                <span className="group-dashboard__stat-suffix">
                  /{totalCapacity}
                </span>
              </span>
              <div
                className="group-dashboard__progress"
                role="progressbar"
                aria-valuenow={capacityUsedPct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="group-dashboard__progress-fill"
                  style={{ width: `${capacityUsedPct}%` }}
                />
              </div>
            </div>
          )}
          {isElimination && estimatedDrawSize > 0 && (
            <div className="group-dashboard__stat">
              <span className="group-dashboard__stat-label">Bracket configurado</span>
              <span className="group-dashboard__stat-value">Ronda {estimatedDrawSize}</span>
              <span className="group-dashboard__stat-meta">
                {elimination.overCapacity
                  ? `${localParticipantsCount} jugadores exceden la capacidad; ajusta Formato`
                  : elimination.preliminaryMatches > 0
                    ? `${elimination.preliminaryMatches} preliminar · ${elimination.directAdvancers} pasan directo a Ronda ${elimination.nextRoundSize}`
                    : "Sin ronda preliminar"}
              </span>
            </div>
          )}
        </div>
        {setupComplete ? (
          <p className="group-dashboard__hint group-dashboard__hint--ok">
            Todos los jugadores inscritos están asignados a un grupo.
            {isElimination && estimatedDrawSize > 0
              ? elimination.overCapacity
                ? ` El bracket configurado en Formato es Ronda ${estimatedDrawSize} y no alcanza para ${localParticipantsCount} jugador(es). Regresa a Formato antes de armar.`
                : elimination.preliminaryMatches > 0
                  ? ` Siguiente: revisar el Draw de Ronda ${estimatedDrawSize}: ${elimination.preliminaryMatches} partido(s) en Preliminar y ${elimination.directAdvancers} jugador(es) pasan directo a Ronda ${elimination.nextRoundSize}.`
                  : ` Siguiente: revisar el Draw de Ronda ${estimatedDrawSize}; no hay ronda preliminar.`
              : ""}
          </p>
        ) : unassignedCount > 0 ? (
          <p className="group-dashboard__hint group-dashboard__hint--alert">
            Faltan <strong>{unassignedCount}</strong> jugador(es) por asignar.{" "}
            {singleGroupScopeNotice || totalGroups > 0 ? (
              <button
                type="button"
                className="btn btn--small"
                disabled={autoAssigningAll || autoAssigningMode !== null}
                onClick={() => void handleAssignAllAutomatically()}
              >
                {autoAssigningAll ? "Asignando…" : "Asignarlos en automático"}
              </button>
            ) : (
              "Crea al menos un grupo y asígnalos."
            )}
          </p>
        ) : null}
      </section>
      )}

      {singleGroupScopeNotice ? (
        <section className="card wiz-card grp-step grp-step--note" aria-label="Crear grupos">
          <p className="grp-step__note" data-no-toast>
            {isElimination ? (
              <>Esta categoría es <strong>eliminatoria</strong>: un solo cuadro con todos los inscritos.</>
            ) : (
              <>El formato es <strong>un solo grupo</strong>: lo creamos por ti y entran todos los inscritos.</>
            )}{" "}
            Al pulsar «Continuar a Draw» (o «Asignarlos en automático») se asigna a todos. Si quieres 2 o más grupos,
            cambia el formato en{" "}
            <Link to={`/admin/tournaments/${tournamentId}/setup/format${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}>
              Formato
            </Link>
            .
            {guardDivision ? (
              <span className="group-create-category">
                {" "}Se crea en <strong>{guardDivision.name}</strong>.
              </span>
            ) : null}
          </p>
        </section>
      ) : (
        <section className="card wiz-card grp-step" aria-label="Crear grupos">
          <div className="grp-step__head">
            <span className="grp-step__num" aria-hidden="true">1</span>
            <div>
              <h2>Crear grupos</h2>
              <p>Solo cuando el formato tiene más de un grupo.</p>
            </div>
          </div>
          <div className="grp-create">
            <label className="grp-create__field">
              Número de grupos
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={groupCountInput}
                placeholder={String(defaultGroupCount)}
                onChange={(event) => {
                  groupCountTouchedRef.current = true;
                  setGroupCountInput(event.target.value);
                }}
                disabled={savingGroup}
              />
            </label>
            <label className="grp-create__field">
              {isDoublesTournament ? "Parejas por grupo" : "Jugadores por grupo"}
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={newGroupCapacity}
                placeholder="Sin límite"
                onChange={(event) => {
                  groupCountTouchedRef.current = true;
                  setNewGroupCapacity(event.target.value);
                }}
                disabled={savingGroup}
              />
            </label>
            <div className="grp-create__actions">
              <button
                type="button"
                className="grp-pill grp-pill--solid"
                disabled={savingGroup || targetGroupCount === null || targetGroupCount === activeGroupsForAssignment.length}
                onClick={() => {
                  if (targetGroupCount !== null) void handleSetGroupCount(targetGroupCount);
                }}
              >
                {savingGroup
                  ? "Guardando…"
                  : targetGroupCount === null
                    ? "Escribe un número"
                    : activeGroupsForAssignment.length === 0 || targetGroupCount === activeGroupsForAssignment.length
                      ? `Crear ${targetGroupCount} ${targetGroupCount === 1 ? "grupo" : "grupos"}`
                      : `Cambiar a ${targetGroupCount} ${targetGroupCount === 1 ? "grupo" : "grupos"}`}
              </button>
              <button
                type="button"
                className="grp-pill grp-pill--outline"
                disabled={savingGroup}
                onClick={() => void createGroups(1)}
              >
                + Agregar 1 grupo
              </button>
            </div>
          </div>
          <p className="grp-create__info" data-no-toast>
            {activeGroupsForAssignment.length > 0
              ? `Ya creados: ${activeGroupsForAssignment.map((group) => tournamentGroupLabel(group, repeatedGroupNames)).join(", ")} (abajo en azul). Cambiar el número agrega o quita grupos vacíos; nunca saca a un jugador asignado.`
              : "Escribe cuántos grupos quieres y cuántos caben en cada uno (vacío = sin límite)."}
            {guardDivision ? (
              <span className="group-create-category">
                {" "}Se crean en <strong>{guardDivision.name}</strong>.
              </span>
            ) : null}
          </p>
        </section>
      )}

      <section className="card wiz-card grp-step grp-board-card group-bulk-assign-card" aria-label={`Asignar ${participantPlural}`}>
        <div className="grp-step__head">
          <span className="grp-step__num" aria-hidden="true">2</span>
          <div>
            <h2>Asignar {participantPlural}</h2>
            <p>A mano (marca y asigna en el grupo) o automático. Los grupos azules son la única vista de grupos.</p>
          </div>
          {assignedInScopeCount > 0 && !reshuffleAll ? (
            <details className="grp-menu grp-step__menu">
              <summary aria-label="Más acciones de asignación">⋯</summary>
              <div className="grp-menu__list">
                <button
                  type="button"
                  className="grp-menu__item"
                  disabled={assigning || groupDrawDone || reshuffleBlockedByMatches}
                  onClick={(event) => {
                    (event.currentTarget.closest("details") as HTMLDetailsElement | null)?.removeAttribute("open");
                    void handleStartReshuffle();
                  }}
                >
                  Repartir desde cero
                </button>
                {groupDrawDone ? (
                  <p className="grp-menu__note" data-no-toast>No disponible: la categoría ya se sorteó.</p>
                ) : reshuffleBlockedByMatches ? (
                  <p className="grp-menu__note" data-no-toast>Ya hay partidos de grupo: para mover jugadores usa «Cambiar de grupo».</p>
                ) : null}
              </div>
            </details>
          ) : null}
        </div>

        {allAssignedFolded ? (
          <div className="grp-done" role="status">
            <span className="grp-done__chip">✓ 0 sin grupo</span>
            <span>
              Todos tienen grupo.{setupMode ? " Ya puedes continuar a Draw." : ""} Para corregir, usa «Editar» en cada
              {isDoublesTournament ? " pareja" : " jugador"}.
            </span>
          </div>
        ) : (
          <div className="grp-assign">
            <div className="grp-pending" aria-label={`${participantPluralTitle} sin grupo`}>
              {unassignedCount > 0 ? (
                <>
                  <div className="grp-pending__head">
                    <strong>Sin grupo · {unassignedCount}</strong>
                    <span className={`grp-pending__count${selectedCount > 0 ? " is-active" : ""}`} aria-live="polite">
                      {selectedCount === 1 ? "1 marcado" : `${selectedCount} marcados`}
                    </span>
                  </div>
                  <input
                    type="search"
                    className="grp-pending__search"
                    placeholder={isDoublesTournament ? "Buscar pareja" : "Buscar jugador"}
                    aria-label={isDoublesTournament ? "Buscar pareja" : "Buscar jugador"}
                    value={pendingSearch}
                    onChange={(event) => setPendingSearch(event.target.value)}
                  />
                  <div className="grp-pending__tools">
                    <button
                      type="button"
                      className="grp-link"
                      onClick={selectAllUnassigned}
                      disabled={assigning || pendingVisible.length === 0}
                    >
                      Seleccionar todos
                    </button>
                    <button
                      type="button"
                      className="grp-link"
                      onClick={clearParticipantSelection}
                      disabled={assigning || selectedCount === 0}
                    >
                      Limpiar
                    </button>
                  </div>
                  {staleInScope.length > 0 ? (
                    <div className="grp-stale" role="region" aria-label="Sin registro en el torneo">
                      <strong>
                        {staleInScope.length === 1
                          ? "1 ya no debería jugar"
                          : `${staleInScope.length} ya no deberían jugar`}
                      </strong>
                      <span className="grp-stale__text">
                        Se eliminaron o rechazaron en Inscripciones, pero siguen contando aquí. Quítalos del torneo para poder avanzar.
                      </span>
                      <ul className="grp-stale__list">
                        {staleInScope.map((p) => (
                          <li key={p.id} className="grp-stale__item">
                            <span className="grp-stale__name">
                              {participantLabel(p)}
                              <small>{staleRegistrations[String(p.id)]}</small>
                            </span>
                            <button
                              type="button"
                              className="button button--sm button--ghost registration-reject-button"
                              onClick={() => void handleRemoveStale(p)}
                              disabled={removingStaleId !== null || assigning}
                            >
                              {removingStaleId === p.id ? "Quitando…" : "Quitar del torneo"}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className="grp-pending__list" role="list">
                    {pendingVisible.length === 0 ? (
                      <p className="muted">Nadie coincide con la búsqueda.</p>
                    ) : (
                      pendingVisible.map((p) => {
                        const selected = selectedParticipantIds.includes(p.id);
                        const ineligible = eligibilityReason(p);
                        const seed = p.initial_rank ?? p.tournament_rank ?? null;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            role="listitem"
                            className={`grp-pend${selected ? " is-selected" : ""}${ineligible ? " is-ineligible" : ""}`}
                            title={ineligible ?? [participantFullLabel(p), chipMeta(p)].filter(Boolean).join(" · ")}
                            onClick={() => toggleParticipantSelection(p.id)}
                            aria-pressed={selected}
                            disabled={assigning}
                          >
                            <span className="grp-pend__check" aria-hidden="true">{selected ? "✓" : ""}</span>
                            <span className="grp-pend__seed">{seed != null ? seed : "S/R"}</span>
                            <span className="grp-pend__name">{participantLabel(p)}</span>
                            {ineligible ? <span className="grp-pend__flag">{ineligible}</span> : null}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <span className="grp-pending__hint">
                    {selectedCount > 0
                      ? `Toca «Asignar ${selectedCount} aquí» en el grupo azul.`
                      : `Marca ${participantPlural} y toca «Asignar aquí» en el grupo azul.`}
                  </span>
                </>
              ) : (
                <p className="grp-pending__hint">
                  Desde cero: entran los {assignedInScopeCount} de la categoría que ya tienen grupo.
                </p>
              )}
            </div>

            {showPublicDraw ? (
              <div className="grp-auto2 grp-auto2--draw">{drawCard}</div>
            ) : activeGroupsForAssignment.length === 0 ? (
              <div className="grp-auto2">
                <strong>Reparto automático</strong>
                <span className="grp-auto2__text">Crea al menos un grupo activo para asignar {participantPlural}.</span>
              </div>
            ) : (
              <div
                className="grp-auto2"
                role={autoPreview ? "region" : undefined}
                aria-label={autoPreview ? "Vista previa del reparto" : undefined}
              >
                <strong>Reparto automático{reshuffleAll ? " · desde cero" : ""}</strong>
                <span className="grp-auto2__text">
                  {reshuffleAll
                    ? "Se reparten de nuevo TODOS los de la categoría (también los ya asignados)."
                    : "Este torneo no tiene sorteo público: el reparto se hace aquí. «Por rank» usa la misma regla que el sorteo público (serpentina por seed y S/R al azar), pero sin acta ni aviso a jugadores."}
                </span>
                <span className="grp-auto2__scope">Aplica sobre {autoAssignScopeLabel}.</span>
                {autoPreview ? (
                  <>
                    <span className="grp-auto2__text grp-auto2__text--strong">
                      {autoPreview.mode === "rank" ? "Por rank" : "Al azar"}: vista previa en los grupos de abajo (líneas
                      punteadas). Nada se guarda hasta «Confirmar reparto».
                    </span>
                    <div className="grp-auto2__actions">
                      <button
                        type="button"
                        className="grp-pill grp-pill--solid"
                        disabled={assigning}
                        onClick={() => void applyAutoPreview()}
                      >
                        {assigning ? "Guardando…" : "Confirmar reparto"}
                      </button>
                      <button
                        type="button"
                        className="grp-pill grp-pill--outline"
                        disabled={assigning}
                        onClick={() => void handleAutoAssign(autoPreview.mode)}
                      >
                        Otra vez
                      </button>
                      <button type="button" className="grp-link" disabled={assigning} onClick={() => setAutoPreview(null)}>
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="grp-auto2__actions">
                    <button
                      type="button"
                      className="grp-pill grp-pill--solid"
                      disabled={autoAssignDisabled}
                      onClick={() => void handleAutoAssign("rank")}
                    >
                      {autoAssigningMode === "rank" ? "Asignando…" : "Por rank (recomendado)"}
                    </button>
                    <button
                      type="button"
                      className="grp-pill grp-pill--outline"
                      disabled={autoAssignDisabled}
                      onClick={() => void handleAutoAssign("random")}
                    >
                      {autoAssigningMode === "random" ? "Asignando…" : "Al azar"}
                    </button>
                  </div>
                )}
                {reshuffleAll ? (
                  <button
                    type="button"
                    className="grp-link"
                    disabled={assigning}
                    onClick={() => setReshuffleAll(false)}
                  >
                    Salir de «desde cero»
                  </button>
                ) : null}
              </div>
            )}
          </div>
        )}
        {allAssignedFolded && drawCard ? <div className="grp-assign__draw">{drawCard}</div> : null}

        {loading && visibleGroups.length === 0 ? (
          <p className="muted">Cargando grupos…</p>
        ) : visibleGroups.length === 0 ? (
          <p className="muted grp-ncards__empty">
            Aún no hay grupos.{singleGroupScopeNotice ? " Se crea solo al continuar." : " Créalos arriba en «Crear grupos»."}
          </p>
        ) : (
          <div className="grp-ncards" ref={targetsRef} tabIndex={-1} aria-label="Grupos">
            {visibleGroups.map((group) => {
              // «Mover a…»: solo grupos de la categoría elegida arriba.
              const activeGroups = activeGroupsForAssignment.filter((g) => g.id !== group.id);
              const participantCount = group.participants?.length ?? 0;
              const sortedParticipants = sortGroupAssignments(group);
              // Eduardo 2026-10-08: el admin acomoda grupos también por club.
              const showClub = sortedParticipants.some((a) => a.participant && participantClubLabel(a.participant) !== "-");
              // Round Robin: «Cambiar de grupo…» (acomoda también los partidos).
              const roundRobinGroup = isRoundRobinGroup(group);
              const changeBlockedReason = roundRobinGroup ? changeGroupBlockedReason(group) : null;
              const changeTargets = roundRobinGroup ? sameCategoryTargets(group) : [];
              // «Actual #N» solo cuando la categoría ya tiene resultados: alguien de
              // sus grupos ya jugó (resultado o W.O.), el mismo dato que bloquea
              // «Reemplazar jugador».
              const categoryHasResults = groups.some(
                (candidate) =>
                  (candidate.division_id ?? null) === (group.division_id ?? null)
                  && (candidate.participants ?? []).some((item) => playedIds.has(item.participant_id)),
              );
              const groupCapacity = group.capacity;
              const isFull =
                groupCapacity != null && participantCount >= groupCapacity;
              const isOver = groupOverCapacity(groupCapacity, participantCount);
              const label = tournamentGroupLabel(group, repeatedGroupNames);
              const incoming = preview?.incoming.get(group.id) ?? [];
              const leavingHere = sortedParticipants.filter((a) => preview?.leaving.has(a.participant_id)).length;
              const projected = participantCount - leavingHere + incoming.length;
              const free = groupFreeSpots(groupCapacity, participantCount);
              const tooMany = free != null && selectedCount > free;
              const blockedReason = !group.is_active || selectedCount === 0
                ? null
                : free === 0
                  ? "Grupo lleno."
                  : tooMany
                    ? `Solo ${free === 1 ? "cabe 1" : `caben ${free}`}: quita ${selectedCount - (free ?? 0)} de la selección.`
                    : null;
              const justAssigned = lastAssigned?.groupId === group.id ? lastAssigned.count : null;
              const badge = groupCapacity != null && groupCapacity > 0
                ? `${preview ? projected : participantCount}/${groupCapacity}`
                : `${preview ? projected : participantCount}`;
              return (
                <article
                  className={
                    "grp-ncard" +
                    (group.is_active ? "" : " is-inactive") +
                    (selectedCount > 0 && group.is_active && !blockedReason ? " is-ready" : "") +
                    (justAssigned ? " is-just-assigned" : "")
                  }
                  key={group.id}
                  aria-label={label}
                >
                  <div className="grp-ncard__head">
                    <strong className="grp-ncard__name">
                      {label}
                      {!group.is_active ? <span className="grp-ncard__tag">inactivo</span> : null}
                    </strong>
                    <span
                      className={`grp-ncard__cap${isOver ? " is-over" : isFull ? " is-full" : ""}`}
                      title={groupCapacity != null && groupCapacity > 0 ? `Cupo: ${groupCapacity}` : "Sin límite"}
                    >
                      {badge}
                    </span>
                  </div>
                  {isOver ? <span className="grp-over-badge">excede el cupo</span> : null}
                  {token ? (
                    // Torneo 71: partidos de quien ya no está en el grupo (o
                    // repetidos) que siguen «por jugar».
                    <GroupStrayMatchesNotice
                      token={token}
                      tournamentId={tournamentId}
                      groupId={group.id}
                      groupName={label}
                      matches={group.ghost_matches}
                      canEdit={arrangementByDivision[arrangementKey(group.division_id)]?.can_edit !== false}
                      onDone={async (text) => {
                        setError(null);
                        setMessage(text);
                        await reloadData();
                      }}
                      onError={(text) => setError(text)}
                    />
                  ) : null}
                  {participantCount === 0 && incoming.length === 0 ? (
                    <p className="grp-ncard__empty">Todavía sin {participantPlural}.</p>
                  ) : null}
                  {participantCount > 0 ? (
                    <div
                      className={`grp-rows${showClub ? " grp-rows--club" : ""}`}
                      role="list"
                      aria-label={`Jugadores de ${tournamentGroupLabel(group, repeatedGroupNames)}`}
                    >
                      {sortedParticipants.map((a) => {
                        const isExpanded = Boolean(expandedRows[a.participant_id]);
                        const name = participantLabel(a.participant);
                        const editorId = `grp-ed-${group.id}-${a.participant_id}`;
                        const initialRank = a.participant?.initial_rank ?? null;
                        const shownCurrent = a.participant?.current_rank ?? initialRank;
                        const useMoveTo = !roundRobinGroup || plainMoveAllowedInStartedCategory(group, a.participant_id);
                        const alreadyPlayed = playedIds.has(a.participant_id);
                        const leaving = Boolean(preview?.leaving.has(a.participant_id));
                        return (
                          <div role="listitem" className={`grp-row${isExpanded ? " is-open" : ""}${leaving ? " is-leaving" : ""}`} key={a.id}>
                            <div className="grp-row__line">
                              <div className="grp-row__name" title={participantFullLabel(a.participant)}>
                                <strong>{name}</strong>
                                {leaving ? <small className="grp-row__leaving">sale en la vista previa</small> : null}
                                {a.participant?.current_category?.name ? (
                                  <small className="muted grp-row__cat">{a.participant.current_category.name}</small>
                                ) : null}
                              </div>
                              {showClub ? (
                                <span className="grp-row__club">
                                  {a.participant ? participantClubLabel(a.participant) : "-"}
                                </span>
                              ) : null}
                              <span className="grp-row__ranks">
                                <span className="grp-rank" title="Rank inicial">
                                  {initialRank != null ? `Inicial #${initialRank}` : "S/R"}
                                </span>
                                {categoryHasResults ? (
                                  <>
                                    <span className="grp-rank grp-rank--act" title="Rank actual">
                                      {/* Sin rank actual calculado, el actual es el inicial. */}
                                      {shownCurrent != null ? `Actual #${shownCurrent}` : "Actual S/R"}
                                    </span>
                                    {/* Solo si subió o bajó: el «—» de «sin cambio» era ruido junto a las fichas. */}
                                    {a.participant?.rank_movement === "up" || a.participant?.rank_movement === "down" ? (
                                      <RankMovementBadge
                                        movement={a.participant.rank_movement}
                                        delta={a.participant.rank_delta ?? null}
                                      />
                                    ) : null}
                                  </>
                                ) : null}
                              </span>
                              <button
                                type="button"
                                className="grp-btn grp-btn--outline grp-row__toggle"
                                aria-expanded={isExpanded}
                                aria-controls={editorId}
                                aria-label={`${isExpanded ? "Cerrar" : "Editar"} ${name}`}
                                onClick={() =>
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [a.participant_id]: !prev[a.participant_id],
                                  }))
                                }
                              >
                                {isExpanded ? "Cerrar" : "Editar"}
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="grp-ed" id={editorId}>
                                <section className="grp-ed__block" aria-label="Rank">
                                  <div className="grp-ed__fields">
                                    <div className="grp-ed__field">
                                      <span className="grp-ed__label">
                                        <label htmlFor={`${editorId}-ini`}>Rank inicial</label>
                                        <span
                                          className="grp-ed__help"
                                          role="img"
                                          aria-label="El rank con el que entró al grupo. Sirve para sembrar y para el último desempate."
                                          title="El rank con el que entró al grupo. Sirve para sembrar y para el último desempate."
                                        >
                                          ?
                                        </span>
                                      </span>
                                      <input
                                        id={`${editorId}-ini`}
                                        type="number"
                                        min={1}
                                        placeholder="#"
                                        className="grp-input grp-input--rank"
                                        value={
                                          initialRankInputs[a.participant_id] ??
                                          (initialRank != null ? String(initialRank) : "")
                                        }
                                        onChange={(e) =>
                                          setInitialRankInputs((prev) => ({
                                            ...prev,
                                            [a.participant_id]: e.target.value,
                                          }))
                                        }
                                        disabled={savingRanks[a.participant_id]}
                                      />
                                    </div>
                                    {categoryHasResults ? (
                                      <div className="grp-ed__field">
                                        <span className="grp-ed__label">
                                          <label htmlFor={`${editorId}-act`}>Rank actual</label>
                                        </span>
                                        <input
                                          id={`${editorId}-act`}
                                          type="number"
                                          min={1}
                                          placeholder="#"
                                          className="grp-input grp-input--rank"
                                          value={
                                            currentRankInputs[a.participant_id] ??
                                            (a.participant?.current_rank != null
                                              ? String(a.participant.current_rank)
                                              : "")
                                          }
                                          onChange={(e) =>
                                            setCurrentRankInputs((prev) => ({
                                              ...prev,
                                              [a.participant_id]: e.target.value,
                                            }))
                                          }
                                          disabled={savingRanks[a.participant_id]}
                                        />
                                      </div>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="grp-btn grp-btn--solid"
                                      onClick={() => {
                                        if (a.participant) {
                                          void handleSaveRanks(a.participant);
                                        }
                                      }}
                                      disabled={!a.participant || savingRanks[a.participant_id]}
                                    >
                                      {savingRanks[a.participant_id] ? "Guardando…" : "Guardar"}
                                    </button>
                                    <span className="grp-ed__today">
                                      Hoy: {initialRank != null ? `#${initialRank}` : "S/R"}
                                    </span>
                                  </div>
                                </section>

                                <section className="grp-ed__block grp-ed__block--sep" aria-label="Mover o reemplazar">
                                  <strong className="grp-ed__title">Mover o reemplazar</strong>
                                  <div className="grp-ed__pair">
                                    {useMoveTo ? (
                                      <div className="grp-ed__option">
                                        <div className="grp-ed__fields">
                                          <div className="grp-ed__field grp-ed__field--grow">
                                            <span className="grp-ed__label">
                                              <label htmlFor={`${editorId}-mover`}>Mover a</label>
                                            </span>
                                            <select
                                              id={`${editorId}-mover`}
                                              className="grp-input"
                                              value={moveTargets[a.participant_id] ?? ""}
                                              onChange={(e) =>
                                                setMoveTargets((prev) => ({
                                                  ...prev,
                                                  [a.participant_id]: e.target.value ? Number(e.target.value) : "",
                                                }))
                                              }
                                            >
                                              <option value="">Mover a…</option>
                                              {activeGroups.map((g) => (
                                                <option key={g.id} value={g.id}>
                                                  {tournamentGroupLabel(g, repeatedGroupNames)}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <button
                                            type="button"
                                            className="grp-btn grp-btn--outline"
                                            onClick={() => void handleMove(a.participant_id)}
                                            disabled={!moveTargets[a.participant_id] || activeGroups.length === 0}
                                          >
                                            Mover
                                          </button>
                                        </div>
                                        <input
                                          type="text"
                                          className="grp-input"
                                          aria-label="Motivo del cambio"
                                          placeholder="Motivo (opcional)"
                                          value={moveReasons[a.participant_id] ?? ""}
                                          onChange={(e) =>
                                            setMoveReasons((prev) => ({
                                              ...prev,
                                              [a.participant_id]: e.target.value,
                                            }))
                                          }
                                        />
                                        <span className="grp-ed__note">
                                          {roundRobinGroup
                                            ? "Aún no tiene partidos de grupo: se mueve directo."
                                            : "Pasa al jugador a otro grupo."}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="grp-ed__option">
                                        <button
                                          type="button"
                                          className="grp-btn grp-btn--outline"
                                          onClick={() => setChangeGroupFor({ participantId: a.participant_id, groupId: group.id })}
                                          disabled={changeBlockedReason !== null || changeTargets.length === 0}
                                          title={
                                            changeBlockedReason === null && changeTargets.length === 0
                                              ? "No hay otro grupo activo en esta categoría."
                                              : undefined
                                          }
                                        >
                                          Cambiar de grupo
                                        </button>
                                        {changeBlockedReason ? (
                                          <span className="grp-ed__note group-row-editor__hint" data-no-toast>
                                            {changeBlockedReason}
                                          </span>
                                        ) : (
                                          <span className="grp-ed__note">Pasa al jugador a otro grupo con sus datos.</span>
                                        )}
                                      </div>
                                    )}
                                    <div className="grp-ed__option">
                                      <button
                                        type="button"
                                        className="grp-btn grp-btn--outline"
                                        onClick={() => setReplaceFor(a.participant_id)}
                                        disabled={alreadyPlayed}
                                        title={
                                          alreadyPlayed
                                            ? "Ya jugó (o tiene un resultado o W.O.): no se puede reemplazar."
                                            : "Otro jugador toma su lugar con los mismos partidos, fechas y canchas"
                                        }
                                      >
                                        Reemplazar jugador
                                      </button>
                                      <span className="grp-ed__note" data-no-toast>
                                        {alreadyPlayed
                                          ? "Ya jugó: no se puede reemplazar."
                                          : "Otro jugador toma su lugar y sus partidos."}
                                      </span>
                                    </div>
                                  </div>
                                </section>

                                <section className="grp-ed__block grp-ed__danger" aria-label="Quitar del grupo">
                                  <strong className="grp-ed__title grp-ed__title--danger">Quitar del grupo</strong>
                                  <span className="grp-ed__note">Sus partidos pendientes se cancelan.</span>
                                  <div className="grp-ed__fields">
                                    <select
                                      className="grp-input grp-input--reason"
                                      aria-label="Motivo de baja"
                                      value={removeReasons[a.participant_id] ?? ""}
                                      onChange={(e) =>
                                        setRemoveReasons((prev) => ({
                                          ...prev,
                                          [a.participant_id]: e.target.value,
                                        }))
                                      }
                                    >
                                      <option value="">Motivo de baja…</option>
                                      {REMOVE_REASONS.map((reason) => (
                                        <option key={reason} value={reason}>{reason}</option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      className="grp-btn grp-btn--danger"
                                      onClick={() => void handleRemove(a.participant_id)}
                                    >
                                      Quitar
                                    </button>
                                  </div>
                                </section>

                                {rowFeedback[a.participant_id] ? (
                                  <p
                                    className={`group-row-editor__feedback${
                                      rowFeedback[a.participant_id].tone === "error" ? " is-error" : " is-ok"
                                    }`}
                                    role={rowFeedback[a.participant_id].tone === "error" ? "alert" : "status"}
                                    data-no-toast
                                  >
                                    {rowFeedback[a.participant_id].text}
                                  </p>
                                ) : null}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {incoming.length > 0 ? (
                    <ul className="grp-ncard__new" aria-label={`Entran a ${label} (vista previa)`}>
                      {incoming.map((item) => (
                        <li key={item.participantId}>
                          <span className="grp-ncard__new-tag">{item.fromLabel ? `de ${item.fromLabel}` : "nuevo"}</span>
                          <span className="grp-ncard__new-name">{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {justAssigned ? (
                    <p className="grp-ncard__done" role="status">
                      ✓ {justAssigned === 1
                        ? `1 ${isDoublesTournament ? "pareja asignada" : "jugador asignado"}`
                        : `${justAssigned} ${participantPlural} asignados`} aquí.
                    </p>
                  ) : null}
                  <div className="grp-ncard__foot">
                    {!group.is_active ? (
                      <button
                        type="button"
                        className="grp-ncard__assign"
                        onClick={() => void handleReactivateGroup(group.id)}
                      >
                        Reactivar
                      </button>
                    ) : selectedCount > 0 ? (
                      <button
                        type="button"
                        className="grp-ncard__assign"
                        disabled={assigning || blockedReason !== null}
                        aria-label={`Asignar ${selectedCount} aquí: ${label}`}
                        onClick={() => void handleAssignToGroup(group.id)}
                      >
                        {assigning ? "Asignando…" : `Asignar ${selectedCount} aquí`}
                      </button>
                    ) : (
                      <span className="grp-ncard__hint">
                        {isFull ? "Grupo lleno." : unassignedCount > 0 ? `Marca ${participantPlural} para asignarlos aquí.` : ""}
                      </span>
                    )}
                    <div className="grp-ncard__tools">
                      {token ? (
                        <GroupEditForm
                          token={token}
                          tournamentId={tournamentId}
                          groupId={group.id}
                          name={group.name}
                          capacity={group.capacity}
                          memberCount={participantCount}
                          onSaved={handleGroupEdited}
                        />
                      ) : null}
                      {group.is_active ? (
                        <details className="grp-menu">
                          <summary aria-label={`Más acciones de ${label}`}>⋯</summary>
                          <div className="grp-menu__list">
                            <button
                              type="button"
                              className="grp-menu__item"
                              onClick={() => void handleDeactivateGroup(group.id)}
                            >
                              Desactivar grupo
                            </button>
                            {participantCount === 0 ? (
                              <button
                                type="button"
                                className="grp-menu__item grp-menu__item--danger"
                                onClick={() => void handleDeleteGroup(group.id)}
                              >
                                Eliminar grupo
                              </button>
                            ) : null}
                          </div>
                        </details>
                      ) : participantCount === 0 ? (
                        <button
                          type="button"
                          className="grp-link grp-link--light"
                          onClick={() => void handleDeleteGroup(group.id)}
                        >
                          Eliminar
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {blockedReason && blockedReason !== "Grupo lleno." ? (
                    <p className="grp-ncard__blocked">{blockedReason}</p>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}

        {selectedCount > 0 ? (
          <div className="grp-selbar" role="region" aria-label="Selección">
            <span className="grp-selbar__count">
              {selectedCount === 1 ? "1 seleccionado" : `${selectedCount} seleccionados`}
            </span>
            <button
              type="button"
              className="btn btn--small grp-selbar__go"
              onClick={() => {
                targetsRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
                targetsRef.current?.focus?.({ preventScroll: true });
              }}
            >
              Ir a grupos ↓
            </button>
            <button type="button" className="btn btn--ghost btn--small" onClick={clearParticipantSelection} disabled={assigning}>
              Quitar selección
            </button>
          </div>
        ) : null}
      </section>

      <p className="grp-notify-note" data-no-toast>
        {showPublicDraw
          ? "Con sorteo público: la fecha sale en la convocatoria y se avisa «Sorteo realizado» (app, correo, WhatsApp) con enlace al acta."
          : "Sin sorteo público: no hay aviso «Sorteo realizado» ni página de acta; los jugadores ven su grupo cuando se publica el torneo."}
      </p>

      {changeGroupFor ? renderChangeGroupDialog(changeGroupFor) : null}
      {replaceFor !== null && token ? (
        <ReplacePlayerDialog
          token={token}
          tournamentId={tournamentId}
          participantId={replaceFor}
          onClose={() => setReplaceFor(null)}
          onDone={(result) => {
            setReplaceFor(null);
            setError(null);
            setMessage(result.summary);
            void reloadData();
          }}
        />
      ) : null}

      <details className="card wiz-card group-accordion movement-history-accordion">
        <summary className="group-accordion__summary">
          <span className="group-accordion__title-wrap">
            <span className="group-accordion__title">Historial de movimientos</span>
            <span className="group-accordion__meta">
              {history.length} movimiento(s) registrados
            </span>
          </span>
          <span className="group-accordion__chevron" aria-hidden="true">
            ▾
          </span>
        </summary>
        <div className="group-accordion__body">
          {history.length === 0 ? (
            <p className="muted">Sin movimientos registrados.</p>
          ) : (
            <ul className="list">
              {history.map((entry) => (
                <li key={entry.id}>
                  <strong>{formatAction(entry.action)}</strong>
                  <span className="muted">
                    {" "}· jugador #{entry.participant_id}
                  </span>
                  <span className="muted">
                    {" "}· {describeGroup(entry.from_group_id)} →{" "}
                    {describeGroup(entry.to_group_id)}
                  </span>
                  {entry.reason && (
                    <span className="muted"> · {entry.reason}</span>
                  )}
                  {entry.created_at && (
                    <span className="muted">
                      {" "}·{" "}
                      {formatDateTime(entry.created_at)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
      {setupMode ? (
        <div className="wiz-bottombar wiz-bottombar--brand fmt-bottombar grp-bottombar">
          <Link
            className="wiz-bottombar__back"
            to={`/admin/tournaments/${tournamentId}/setup/ranking${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}
          >
            ‹ Volver a Ranking
          </Link>
          <div className="fmt-bottombar__actions">
            <FieldHint label="¿Cuándo continúo a Draw?">
              Cuando hayas creado los grupos y asignado a los jugadores,
              continúa a Draw para definir enfrentamientos.
            </FieldHint>
            <button
              className="wiz-bottombar__next fmt-bottombar__next"
              type="button"
              onClick={handleContinueToRounds}
              disabled={continuingToRounds || !canContinueToRounds}
            >
              {continuingToRounds ? "Validando…" : "Continuar a Draw"}
            </button>
          </div>
        </div>
      ) : (
        <TournamentBottomNav tournamentId={tournamentId} />
      )}
    </div>
  );
}
